import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, getDoc, getDocs, collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase/config';
import {
    LayoutDashboard, FilePlus, FileText, MessageSquare, Users, Settings,
    ShoppingBag, ShoppingCart, Building2, Briefcase, GraduationCap,
    MessageCircle, User, DollarSign, TrendingUp, LogOut, Menu, X, Package,
    Megaphone, Calendar, ChevronRight, Info, Globe, Lock, Search, Link2,
    ImageIcon, BookOpen, Newspaper, Palette, Sparkles, Handshake, BarChart3,
    FileSpreadsheet, RotateCcw, Zap, QrCode, Camera, Gift, Cloud, Phone, ShieldCheck, ScanLine,
    ClipboardList, Percent, HardDriveDownload, Factory, Wallet, Home,
    Hospital, CalendarCheck } from 'lucide-react';   // BellRing: 재내원 안내 메뉴 제거로 미사용 (2026-10-30)
import NotificationBell from './NotificationBell';
import NotificationPermissionModal from './NotificationPermissionModal';
import HospitalCandidatePicker from './clinic/HospitalCandidatePicker';
import HospitalVerificationBanner from './clinic/HospitalVerificationBanner';
import DoctorCallDeskStrip from './clinic/doctorCall/DoctorCallDeskStrip';   // 🔔 원장 호출 데스크 띠 (2026-10-08, 플래그 doctorCall strict)
import DelegationV70ReconfirmModal from './clinic/DelegationV70ReconfirmModal';   // 📜 약관 v7.0 제4조의7 재확인(14항) — 시행 후 1회
import { useDelegationV70, DELEGATION_V70_OPEN_EVENT, delegationV70Subject } from '../utils/delegationV70';
import ClinicDelegationPrompt from './clinic/ClinicDelegationPrompt';   // 🛡️ 처리위탁 부속 확인 2건 로그인 후 1회 모달 (2026-10-08)
import { disableNotifications, setupForegroundListener } from '../services/PushNotificationService';
import { isPathAllowedForMode } from '../utils/billingPolicy';
import { getFeatures, isAllFree, paywallActive, isPgReviewer, isBetaUser, isClinicScanIntakeOn, isClinicPatientsOn, isClinicRecallOn, isDeskReceptionOn, isClinicSubscriptionOn, stageOpen, isTermsV70NoticePeriod, isTermsV71NoticePeriod, isTermsV80NoticePeriod, isTermsV80Announced, getClinicExpectedStatementOpenAtText, isDoctorCallOn, isOutsourceMfOn, isJigLocalOn, isJigWebOn, isDesignResultsOn, isPartnerHomeOn } from '../utils/features';
import { useRoadmapPilot } from '../utils/useRoadmapPilot';   // 🗺️ 진료 후 질문 배지 = 플래그 AND 파일럿 치과(서버 위탁 확인) (2026-10-06)
import { V70_BANNER_TEXT, V70_BANNER_LINK } from '../config/termsV70';
import { V71_BANNER_TEXT, V71_BANNER_LINK } from '../config/termsV71';   // 약관 v7.1 CNC 파트너 개정 공고 띠(공고 10-24 · 시행 11-24)
import { v80BannerText, V80_BANNER_LINK } from '../config/termsV80';   // 약관 v8.0 전면 정비 공고 띠 — 공고 전(config/features.termsV80NoticeAt·termsV80At 없으면 숨김)
import { V328_BANNER_TEXT, V328_BANNER_LINK, V328_DISMISS_KEY_PREFIX, isPrivacyV328BannerPeriod } from '../config/privacyV328';   // 처리방침 v3.2.8 공고 띠(10-16 공고·10-23 시행)
import { V335_BANNER_TEXT, V335_BANNER_LINK, V335_DISMISS_KEY_PREFIX, isPrivacyV335BannerPeriod } from '../config/privacyV335';   // 처리방침 v3.3.5 공고 띠(10-27 공고·11-03 시행, S5 — v3.2.8 전례 그대로)
import { getRoadmapInboxCount } from '../services/careRoadmapApi';   // 🗺️ '환자 관리' 메뉴 배지(확인할 답 건수 — 건수만, 환자명 0) (2026-10-06)
import { useBookingAvailable } from '../utils/useBookingAvailable';   // 📅 「예약」 메뉴 = careBooking 플래그 AND 서버 available(파일럿·위탁 판) — fail-closed (2026-10-11)
import { getClinicBookingInboxCount } from '../services/bookingApi';   // 📅 「예약」 배지(새 요청 건수만 — 환자명 0)
import { isAgentOutdated } from '../config/agentVersion';
import { isAdminUser } from '../utils/adminClaim';
import { BIZ_ADDRESS } from './common/SiteFooter';

// 커뮤니티 항목 (14개) — CommunityHub에서도 재사용
const communityItemsData = [
    // 업체소개는 사이드바 서비스 메뉴에서 접근 (커뮤니티에서 제거)
    { name: '공감마당', icon: Handshake, path: '/empathy-board', desc: '직군 간 소통과 화합을 위한 공간' },
    { name: '자유게시판', icon: MessageSquare, path: '/community-board', desc: '자유로운 정보 교류와 소통 공간' },
    { name: '구인공고', icon: Briefcase, path: '/job-board', desc: '치과·기공소 구인구직 정보' },
    { name: '중고물품', icon: Package, path: '/used-items', desc: '장비 및 재료 중고 거래' },
    { name: '세미나', icon: GraduationCap, path: '/seminars', desc: '세미나 및 교육 행사 안내' },
    { name: '신제품', icon: Package, path: '/new-products', desc: '최신 치과·기공 제품 소개' },
    { name: '전시', icon: ImageIcon, path: '/exhibition', desc: '전시회 및 박람회 정보' },
    { name: '피드백', icon: MessageCircle, path: '/feedback', desc: '서비스 개선을 위한 의견' },
    { name: '학술/임상', icon: BookOpen, path: '/clinical', desc: '학술 자료 및 임상 케이스' },
    { name: '치과뉴스', icon: Newspaper, path: '/dental-news', desc: '치과 업계 최신 뉴스' },
    { name: '보수교육', icon: GraduationCap, path: '/ce-education', desc: '보수교육 일정 및 정보' },
    { name: '정부지원', icon: Building2, path: '/gov-support', desc: '정부 지원 사업 및 제도 안내' },
    { name: '인테리어·개원', icon: Palette, path: '/interior', desc: '개원 준비 및 인테리어 정보' },
    { name: '청소·폐기물', icon: Sparkles, path: '/cleaning', desc: '의료폐기물 처리 및 청소 정보' },
];

// ── 치과/기공소 사이드바 고급 시안 스타일 (시각 전용, 2026-10-21) ──
// 카드 묶음 → 정돈형 플랫 리스트: 섹션 eyebrow 라벨 + 활성 항목만 은은히 강조(좌측 액센트 바·옅은 틴트·액센트 아이콘).
// 토큰은 .dc-pside 로만 한정 — 본문 영역·다른 화면에는 영향 없음(기존 다크모드 지원과 충돌 방지). 화이트+초록 액센트.
const PREMIUM_SIDEBAR_CSS = `
.dc-pside{position:fixed;top:0;left:0;bottom:0;z-index:100;width:236px;display:flex;flex-direction:column;
  background:var(--dc-side);border-right:1px solid var(--dc-line);
  font-family:system-ui,-apple-system,"Segoe UI","Apple SD Gothic Neo",sans-serif;
  --dc-side:#ffffff;--dc-side-ink:#3a4354;--dc-active-bg:#f0f4f2;--dc-active-ink:#0d5c45;
  --dc-accent:#0e7a58;--dc-accent-soft:#e6f2ed;--dc-faint:#aeb6c2;--dc-muted:#8a93a3;
  --dc-ink:#1b2230;--dc-line:#edf0f4;--dc-lock:#9a7b2e;--dc-lock-soft:#f8f1df;}
@media (prefers-color-scheme:dark){.dc-pside{
  --dc-side:#101318;--dc-side-ink:#b7bfcc;--dc-active-bg:#15211c;--dc-active-ink:#5fd3aa;
  --dc-accent:#3cc598;--dc-accent-soft:#14241d;--dc-faint:#5c6472;--dc-muted:#8b93a1;
  --dc-ink:#e9edf3;--dc-line:#1e232c;--dc-lock:#d8b566;--dc-lock-soft:#26210f;color-scheme:dark;}}
.dc-pside .dc-brand{display:flex;align-items:center;gap:10px;padding:16px 14px 14px;border-bottom:1px solid var(--dc-line)}
.dc-pside .dc-mark{width:32px;height:32px;border-radius:9px;background:linear-gradient(145deg,var(--dc-accent),#0b6147);
  display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:15px;flex:none;
  box-shadow:0 2px 8px rgba(14,122,88,.3)}
.dc-pside .dc-brand-wrap{min-width:0;flex:1}
.dc-pside .dc-brand-nm{font-size:15px;font-weight:700;color:var(--dc-ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dc-pside .dc-brand-sub{font-size:10.5px;color:var(--dc-faint);margin-top:-1px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dc-pside .dc-body{flex:1;overflow-y:auto;padding:8px 12px 12px}
.dc-pside .dc-sec{margin-top:12px}
.dc-pside .dc-sec:first-child{margin-top:4px}
.dc-pside .dc-eyebrow{display:flex;align-items:center;gap:6px;font-size:10.5px;font-weight:700;letter-spacing:.1em;
  text-transform:uppercase;color:var(--dc-faint);padding:6px 10px 5px}
.dc-pside .dc-lk{font-size:9.5px;color:var(--dc-lock);background:var(--dc-lock-soft);border-radius:5px;padding:1px 6px;
  letter-spacing:.04em;font-weight:800;display:inline-flex;align-items:center;gap:3px}
.dc-pside .dc-eyebrow-right{margin-left:auto;display:inline-flex;align-items:center;gap:6px}
.dc-pside .dc-item{position:relative;display:flex;align-items:center;gap:11px;width:100%;border:none;background:none;
  font-family:inherit;font-size:13.5px;color:var(--dc-side-ink);cursor:pointer;text-align:left;
  padding:9px 11px;border-radius:9px;margin-bottom:2px;transition:background .13s ease,color .13s ease}
.dc-pside .dc-item:hover{background:var(--dc-active-bg)}
.dc-pside .dc-item .dc-ic{width:17px;height:17px;flex:none;color:var(--dc-faint);display:flex;align-items:center;
  justify-content:center;transition:color .13s ease}
.dc-pside .dc-item .dc-tx{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.dc-pside .dc-item.on{background:var(--dc-active-bg);color:var(--dc-active-ink);font-weight:600}
.dc-pside .dc-item.on .dc-ic{color:var(--dc-accent)}
.dc-pside .dc-item.on::before{content:"";position:absolute;left:-12px;top:50%;transform:translateY(-50%);
  width:3px;height:18px;border-radius:0 3px 3px 0;background:var(--dc-accent)}
.dc-pside .dc-item.muted{color:var(--dc-faint);opacity:.75}
.dc-pside .dc-lockcard{width:100%;text-align:left;padding:12px;border:1px dashed var(--dc-lock);border-radius:10px;
  background:var(--dc-lock-soft);cursor:pointer;font-family:inherit}
.dc-pside .dc-lockcard-t{font-weight:800;font-size:13px;color:var(--dc-lock);display:flex;align-items:center;gap:6px}
.dc-pside .dc-lockcard-d{font-size:11.5px;color:var(--dc-lock);opacity:.85;margin-top:3px}
.dc-pside .dc-foot{border-top:1px solid var(--dc-line);padding:11px 14px;display:flex;align-items:center;gap:10px}
.dc-pside .dc-av{width:30px;height:30px;border-radius:50%;background:var(--dc-accent-soft);color:var(--dc-accent);flex:none;
  display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px}
.dc-pside .dc-foot-who{font-size:12.5px;font-weight:700;color:var(--dc-ink);line-height:1.2;overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap}
.dc-pside .dc-foot-role{font-size:10.5px;color:var(--dc-faint);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dc-pside .dc-foot-actions{margin-left:auto;display:flex;align-items:center;gap:2px}
.dc-pside .dc-foot-btn{padding:4px 5px;border:none;background:transparent;color:var(--dc-faint);cursor:pointer;
  font-size:11.5px;font-weight:600;border-radius:6px;font-family:inherit}
.dc-pside .dc-foot-btn:hover{background:var(--dc-active-bg);color:var(--dc-ink)}
`;

function MainLayout({ children, user: userData, refreshUserData }) {
    const navigate = useNavigate();
    const location = useLocation();

    // 🖥️ 임베드 모드(덴클 프로그램 내장 웹뷰, 0.9.1): /agent-embed 진입 시 세션 플래그
    // 사이드바·상단 네비만 숨긴다 — 핀 잠금 등 가드 로직은 그대로 동작 (이중 메뉴 방지 목적)
    const isEmbedded = (() => {
        try { return sessionStorage.getItem('dcEmbedded') === '1'; } catch (_) { return false; }
    })();

    // 📢 약관 v6.0 개정 고지 배너 (게시 시작일: 2026-08-17 — 30일 고지 기산 근거, 시행 2026-09-17)
    // 수수료 전부 폐지(유리 변경 즉시 시행) + 치과 구독 신설(시행 유보). 닫으면 localStorage에 사용자별로 기억
    const policyBannerKey = `policyBannerV60Dismissed_${userData?.uid || 'anon'}`;
    // 기본 숨김 — uid 로드 후 올바른 키로 재확인(아래 useEffect)해야 닫힘이 안정적으로 유지됨.
    //   (종전: mount 시 uid 미로드 → _anon 키로 읽고, 닫을 땐 _{uid} 키로 저장 → 키 불일치로 닫아도 계속 뜨던 버그)
    // 약관 v6.0 개정 배너 내림 — 대표 지시 2026-08-21 ("위에 나오는 거 안 나오게 지워줘").
    // ⚠️ 고지 기록: 배너 게시 기간 2026-08-17 ~ 2026-08-21 (30일 고지 기산 증빙은 이 주석과 배포 이력으로 보존).
    //   약관 전문 페이지(/terms)의 공고일·시행일 표기는 유지 — 게시 중단이 고지 효력에 영향 없는지는 법적보안 검토 항목.
    //   되살리려면 아래 false를 원래 로직으로 복원.
    const [showPolicyBanner, setShowPolicyBanner] = useState(false);
    const dismissPolicyBanner = () => setShowPolicyBanner(false);
    void policyBannerKey;   // 키는 보존 (재게시 시 닫힘 기억 재사용)

    // 📢 약관 v6.2 · 개인정보처리방침 v3.2.3 공고 안내 띠 (2026-10-04 대표 결정, 문안 docs/공고문안_약관v6.2_처리방침v3.2.3_2026-10-04.md (1))
    // ⚠️ 고지 기록: 게시 기간 2026-10-04 00:00 ~ 2026-11-03 24:00 KST(30일). 기간 중 내리지 말 것(v6.0 4일 만에 내린 전례).
    //   닫기 기억 키는 v6.2 전용(policyBannerV62Dismissed_) — v6.0 키 재사용 금지(그때 닫은 회원에게 안 보이는 고지 누락).
    //   닫아도 /terms·/privacy 전문의 공고일·시행일 표기는 그대로 유지된다.
    const policyV62Key = `policyBannerV62Dismissed_${userData?.uid || 'anon'}`;
    const [policyV62Dismissed, setPolicyV62Dismissed] = useState(true);   // uid 로드 전에는 숨김(키 불일치 방지 — v6.0 버그 교훈)
    useEffect(() => {
        if (!userData?.uid) return;
        try {
            setPolicyV62Dismissed(localStorage.getItem(`policyBannerV62Dismissed_${userData.uid}`) === '1');
        } catch (e) {
            // 저장소 접근 불가(사생활 모드 등) — 고지는 보여주는 쪽으로
            console.error('[약관 v6.2 안내] 닫기 기록 읽기 실패 — 안내 표시:', e?.message || e);
            setPolicyV62Dismissed(false);
        }
    }, [userData?.uid]);
    const dismissPolicyV62 = () => {
        setPolicyV62Dismissed(true);
        try { localStorage.setItem(policyV62Key, '1'); }
        catch (e) { console.error('[약관 v6.2 안내] 닫기 기록 저장 실패:', e?.message || e); }
    };

    // 📢 약관 v7.0 개정 공고 띠 (2026-10-06 대표 결정 — 공고 2026-10-06, 30일 고지, 시행 2026-11-06. 문안 src/config/termsV70.js V70_BANNER_TEXT = 정본 §3-4 (가))
    // ⚠️ 고지 기록: 게시 기간 2026-10-06 00:00 ~ 시행 시각(config/features.termsV70At, 기본 2026-11-06 00:00 KST) 전까지. 기간 중 내리지 말 것(v6.0 4일 만에 내린 전례 — 기산 증빙 끊김).
    //   닫기 기억 키는 v7.0 전용(policyBannerV70Dismissed_). 닫아도 /terms 의 공고·전문은 그대로. 같은 띠를 로그인 화면(Login.js)에도 둔다(비로그인 회원 고지).
    //   기산 증빙(config/policyNotices/notices/terms_v7_0)은 관리자가 공고 당일 생성 — 초안 §3-2(이 컴포넌트는 읽지 않는다).
    const policyV70Key = `policyBannerV70Dismissed_${userData?.uid || 'anon'}`;
    const [policyV70Dismissed, setPolicyV70Dismissed] = useState(true);   // uid 로드 전에는 숨김(키 불일치 방지)
    useEffect(() => {
        if (!userData?.uid) return;
        try {
            setPolicyV70Dismissed(localStorage.getItem(`policyBannerV70Dismissed_${userData.uid}`) === '1');
        } catch (e) {
            console.error('[약관 v7.0 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e);
            setPolicyV70Dismissed(false);
        }
    }, [userData?.uid]);
    const dismissPolicyV70 = () => {
        setPolicyV70Dismissed(true);
        try { localStorage.setItem(policyV70Key, '1'); }
        catch (e) { console.error('[약관 v7.0 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };

    // 📢 약관 v7.1 개정 공고 띠 (CNC 파트너 — 공고 2026-10-24, 30일 고지, 시행 2026-11-24. 문안 src/config/termsV71.js V71_BANNER_TEXT = 정본 §4 공고 상자 문안)
    // ⚠️ 게시 기간 2026-10-24 00:00 ~ 시행 시각(config/features.termsV71At, 기본 2026-11-24 00:00 KST) 전까지. 기간 중 내리지 말 것. v7.0 띠(~11-06)와 겹치는 기간엔 두 띠가 위아래로 함께 보인다.
    //   닫기 기억 키는 v7.1 전용(policyBannerV71Dismissed_). 노출 대상·제외 화면은 v7.0 띠와 같다. 같은 띠를 로그인 화면(Login.js)에도 둔다.
    const policyV71Key = `policyBannerV71Dismissed_${userData?.uid || 'anon'}`;
    const [policyV71Dismissed, setPolicyV71Dismissed] = useState(true);   // uid 로드 전에는 숨김(키 불일치 방지)
    useEffect(() => {
        if (!userData?.uid) return;
        try {
            setPolicyV71Dismissed(localStorage.getItem(`policyBannerV71Dismissed_${userData.uid}`) === '1');
        } catch (e) {
            console.error('[약관 v7.1 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e);
            setPolicyV71Dismissed(false);
        }
    }, [userData?.uid]);
    const dismissPolicyV71 = () => {
        setPolicyV71Dismissed(true);
        try { localStorage.setItem(policyV71Key, '1'); }
        catch (e) { console.error('[약관 v7.1 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };

    // 📢 약관 v8.0 전면 정비 공고 띠 — ★ 공고 전. 게시 기간 = config/features.termsV80NoticeAt ~ termsV80At 전까지(둘 다 있어야 그린다 — utils/features isTermsV80NoticePeriod). 문안 src/config/termsV80.js v80BannerText(features) = 정본 §4-4.
    //   v8.0 공고가 시작되면 v7.1 띠는 내린다(v7.1 공고는 v8.0 공고로 대체 — policyV71Show 참고). 닫기 기억 키는 v8.0 전용(policyBannerV80Dismissed_). 노출 대상·제외 화면은 v7.0 띠와 같다. 같은 띠를 로그인 화면(Login.js)에도 둔다.
    const policyV80Key = `policyBannerV80Dismissed_${userData?.uid || 'anon'}`;
    const [policyV80Dismissed, setPolicyV80Dismissed] = useState(true);   // uid 로드 전에는 숨김(키 불일치 방지)
    useEffect(() => {
        if (!userData?.uid) return;
        try {
            setPolicyV80Dismissed(localStorage.getItem(`policyBannerV80Dismissed_${userData.uid}`) === '1');
        } catch (e) {
            console.error('[약관 v8.0 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e);
            setPolicyV80Dismissed(false);
        }
    }, [userData?.uid]);
    const dismissPolicyV80 = () => {
        setPolicyV80Dismissed(true);
        try { localStorage.setItem(policyV80Key, '1'); }
        catch (e) { console.error('[약관 v8.0 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };

    // 📢 개인정보처리방침 v3.2.8 공고 띠 (대표 10-16 승인 — 공고 2026-10-16 · 시행 2026-10-23. 문안 docs/공고문안_처리방침v3.2.8_2026-10-16.md §3-1, config/privacyV328.js)
    //   게시 기간: 공고일 00:00 ~ 2026-10-30 00:00 KST(법무 권고 — 최소 시행 시각까지, 시행 전에는 시각으로 내려가지 않는다). 기간 중 내리지 말 것.
    //   닫기 기억 키는 v3.2.8 전용(policyBannerV328Dismissed_{uid}) — v6.2·v7.0 키 재사용 금지. 읽기 실패 = 띠 표시. 노출 대상은 v7.0 띠와 같다(policyV328Show).
    //   공고 시각 증빙 config/policyNotices/notices/privacy_v3_2_8 은 관리자가 공고 당일 scripts/create-policy-notice-privacy-v328.js 로 만든다(이 컴포넌트는 읽지 않는다).
    const policyV328Key = `${V328_DISMISS_KEY_PREFIX}${userData?.uid || 'anon'}`;
    const [policyV328Dismissed, setPolicyV328Dismissed] = useState(true);   // uid 로드 전에는 숨김(키 불일치 방지)
    useEffect(() => {
        if (!userData?.uid) return;
        try {
            setPolicyV328Dismissed(localStorage.getItem(`${V328_DISMISS_KEY_PREFIX}${userData.uid}`) === '1');
        } catch (e) {
            console.error('[처리방침 v3.2.8 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e);
            setPolicyV328Dismissed(false);
        }
    }, [userData?.uid]);
    const dismissPolicyV328 = () => {
        setPolicyV328Dismissed(true);
        try { localStorage.setItem(policyV328Key, '1'); }
        catch (e) { console.error('[처리방침 v3.2.8 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };
    // 📢 처리방침 v3.3.5 공고 띠 (S5, 공고 2026-10-27 · 시행 2026-11-03, 게시 끝 2026-11-10 00:00 KST — config/privacyV335.js). v3.2.8 띠와 같은 구조, 닫기 키 v3.3.5 전용(policyBannerV335Dismissed_{uid})
    const policyV335Key = `${V335_DISMISS_KEY_PREFIX}${userData?.uid || 'anon'}`;
    const [policyV335Dismissed, setPolicyV335Dismissed] = useState(true);   // uid 로드 전에는 숨김(키 불일치 방지)
    useEffect(() => {
        if (!userData?.uid) return;
        try {
            setPolicyV335Dismissed(localStorage.getItem(`${V335_DISMISS_KEY_PREFIX}${userData.uid}`) === '1');
        } catch (e) {
            console.error('[처리방침 v3.3.5 공고] 닫기 기록 읽기 실패 — 공고 표시:', e?.message || e);
            setPolicyV335Dismissed(false);
        }
    }, [userData?.uid]);
    const dismissPolicyV335 = () => {
        setPolicyV335Dismissed(true);
        try { localStorage.setItem(policyV335Key, '1'); }
        catch (e) { console.error('[처리방침 v3.3.5 공고] 닫기 기록 저장 실패:', e?.message || e); }
    };
    
    // 현재 경로에서 카테고리 자동 계산
    const activeCategory = useMemo(() => {
        const path = location.pathname;
        const bType = userData?.businessType || userData?.companyBusinessType;
        const isSellerUser = bType === 'seller' || userData?.sellerStatus === 'approved';
        const isClinicOrLab = bType === 'clinic' || bType === 'dental' || bType === '치과' || bType === 'lab' || bType === '기공소' || bType === 'outsource';

        // 관리자 경로 (2026-05 새 그룹: analytics/members/patient/content/broker/settings)
        if (path.includes('/admin/analytics')) return 'analytics';
        if (path.includes('/admin/user-management')
            || path.includes('/admin/lab-advertising')) return 'members';
        if (path.includes('/admin/localdata-sync')) return 'labdata';
        if (path.includes('/admin/hira-sync')
            || path.includes('/admin/care-recommendations')) return 'patient';
        if (path.includes('/admin/used-item-management') || path.includes('/admin/job-management')
            || path.includes('/admin/seminar-management') || path.includes('/admin/feedbacks')) return 'content';
        if (path.includes('/admin/brokerage') || path.includes('/admin/referrals')) return 'broker';
        if (path.includes('/admin/payment-settings') || path.includes('/admin/page-toggle')) return 'settings';
        // 판매자 경로 (v4 스펙 5그룹)
        if (isSellerUser) {
            if (path.includes('/seller-dashboard')) return 'dashboard';
            if (path.includes('/order-management') || path.includes('/seller-orders')) return 'orders';
            if (path.includes('/product-management') || path.includes('/product-registration') || path.includes('/product-edit') || path.includes('/bulk-product-upload')) return 'products';
            if (path.includes('/seller-settlement')) return 'settlement';
            if (path.includes('/seller-store-settings') || path.includes('/profile')) return 'mypage';
            if (path.includes('/seller-application') || path.includes('/seller-referral')) return 'dashboard';
            return 'dashboard';
        }

        // 치과/기공소 사용자: 새 5그룹 구조
        if (isClinicOrLab) {
            // 설정 (settings) — 거래명세서, 통계, 거래처, 직원 포함
            if (path.includes('/transactions') || path.includes('/outsource-statements') || path.includes('/statements') || path.includes('/statistics') || path.includes('/outsource-statistics') || path.includes('/stats') || path.includes('/connections') || path.includes('/company-manager') || path.includes('/profile') || path.includes('/settings') || path.includes('/lab/email-settings') || path.includes('/lab-advertising') || path.includes('/outsource-advertising') || path.includes('/membership')) return 'settings';
            // 서비스 (service)
            if (path.includes('/marketplace') || path.includes('/cart') || path.includes('/my-orders') || path.includes('/community') || path.includes('/empathy-board') || path.includes('/lab-directory')) return 'service';
            // 커뮤니티 하위 페이지도 서비스
            if (path.includes('/community-board') || path.includes('/job-board') || path.includes('/seminars') || path.includes('/feedback') || path.includes('/used-items') || path.includes('/new-products') || path.includes('/gov-support') || path.includes('/exhibition') || path.includes('/clinical') || path.includes('/dental-news') || path.includes('/ce-education') || path.includes('/interior') || path.includes('/cleaning')) return 'service';
            // 핵심 업무 (core) - 기본값
            return 'core';
        }

        // 일반 경로 (관리자/판매자/재료회사/개인회원 등 기존 구조)
        if (path.includes('/lab-directory')) return 'community';
        if (path.includes('/lab-advertising')) return 'findLab';
        if (path.includes('/marketplace') || path.includes('/cart') || path.includes('/my-orders') || path.includes('/lab-warranty')) return 'market';
        if (path.includes('/dashboard') || path.includes('/create-order') || path.includes('/outsource-order') || path.includes('/orders') || path.includes('/calendar')) return 'lab';
        if (path.includes('/marketing-dashboard') || path.includes('/marketer-requests') || path.includes('/marketer-contents') || path.includes('/marketing-images')) return 'marketing';
        if (path.includes('/community') || path.includes('/empathy-board') || path.includes('/community-board') || path.includes('/job-board') || path.includes('/seminars') || path.includes('/feedback') || path.includes('/used-items') || path.includes('/new-products') || path.includes('/gov-support') || path.includes('/exhibition') || path.includes('/clinical') || path.includes('/dental-news') || path.includes('/ce-education') || path.includes('/interior') || path.includes('/cleaning')) return 'community';
        if (path.includes('/subscription') || path.includes('/billing')) return 'settings';
        if (path.includes('/profile') || path.includes('/connections') || path.includes('/company-manager') || path.includes('/transactions') || path.includes('/statistics')) return 'settings';
        if (path.includes('/staff-invitations') || path.includes('/staff-join')) return 'staff';
        return 'lab';
    }, [location.pathname, userData]);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [showPinSetupRequiredModal, setShowPinSetupRequiredModal] = useState(false);
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState('');
    // 해제 상태: 세션 저장 — 같은 탭 새로고침엔 유지, 브라우저/탭 종료 시 자동 잠김 (2026-07-30 회의: 타이머 없음 + 수동 잠그기)
    const [pinAuthenticated, setPinAuthenticatedState] = useState(() => sessionStorage.getItem('dcOwnerUnlocked') === '1');
    const setPinAuthenticated = (v) => {
        setPinAuthenticatedState(v);
        try { v ? sessionStorage.setItem('dcOwnerUnlocked', '1') : sessionStorage.removeItem('dcOwnerUnlocked'); } catch (_) {}
    };
    const [userPin, setUserPin] = useState(null);   // 존재 여부만 사용 (pinSet 플래그 — 평문은 서버로 이전됨)
    const [pinVerifying, setPinVerifying] = useState(false);
    // 핀 분실 재설정 모드: '' | 'code' (코드+새 핀 입력)
    const [pinResetMode, setPinResetMode] = useState('');
    const [pinResetCode, setPinResetCode] = useState('');
    const [pinResetNew, setPinResetNew] = useState('');
    const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
    const [showNotificationModal, setShowNotificationModal] = useState(false);

    // ── 치과 자동 매칭 후보 모달 (가입 직후 sessionStorage에 저장됨) ──
    const [showCandidatePicker, setShowCandidatePicker] = useState(false);
    const [matchCandidates, setMatchCandidates] = useState([]);
    useEffect(() => {
        if (!userData?.uid) return;
        if (userData.businessType !== 'dental') return;
        // 이미 등록된 치과가 있으면 모달 노출 X
        const pending = sessionStorage.getItem('hospitalMatchPending');
        if (pending !== 'true') return;
        try {
            const raw = sessionStorage.getItem('hospitalMatchCandidates');
            const cands = raw ? JSON.parse(raw) : [];
            setMatchCandidates(cands);
            // 살짝 딜레이 (다른 모달과 겹침 방지)
            const t = setTimeout(() => setShowCandidatePicker(true), 1200);
            return () => clearTimeout(t);
        } catch (_) { /* parse 실패 무시 */ }
    }, [userData?.uid, userData?.businessType]);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);

    // ── 🖥️ 프로그램 유도 배너 (2026-08-03 대표 결정 ②) ──
    // 🖥️ 프로그램 유도 배너("덴클 프로그램에서 여는 게 더 편해요") 제거 (대표 지시 2026-08-15)

    const businessType = userData?.businessType || userData?.companyBusinessType;

    // 사용자 유형 판단
    const isBusinessOwner = userData && userData.businessType && !userData.companyId;
    const isStaff = userData && userData.companyId;
    const isIndividual = userData && !userData.businessType && !userData.companyId;
    const isSeller = businessType === 'seller' || userData?.sellerStatus === 'approved';
    const isSupplier = userData?.userType === 'supplier' || businessType === 'material';
    const isAdmin = isAdminUser(userData);
    const isSubscribed = userData?.isSubscribed === true || isAdmin;

    // ── 단계별 오픈 플래그 (config/features) ──
    // phase 1=거래명세서, 2=해외수주, 3=치과 에이전트, 4=매칭·테스트보철
    // 베타(아이반 기공소 + 연결 치과 + 관리자)는 단계 무관 전체 노출
    const [features, setFeatures] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatures(f); });
        return () => { alive = false; };
    }, [userData?.uid]);
    const isBeta = isAdmin || isBetaUser(features, userData, auth.currentUser?.email);

    // 📜 약관 v7.0 제4조의7 14항 재확인 모달 (2026-10-06 코드 준비 — 시행 2026-11-06 후 isTermsV70Effective 로만 required 가 true)
    //   로그인 후 세션당 1회 자동(sessionStorage delegationV70Prompted_{uid}) + 잠금 화면(App.js DelegationV70Gate)의 [확인하기] 이벤트로 재오픈.
    //   대표 [확인] → users.consents.delegation v7.0 기록 → refreshUserData 로 userData 갱신 → required false → 잠금 해제. 직원은 안내만.
    const delegationV70 = useDelegationV70(features, userData);
    const [delegationV70Open, setDelegationV70Open] = useState(false);
    useEffect(() => {
        if (!delegationV70.required || delegationV70.loading || !userData?.uid) return;
        const key = `delegationV70Prompted_${userData.uid}`;
        try {
            if (sessionStorage.getItem(key) === '1') return;
            sessionStorage.setItem(key, '1');
        } catch (e) {
            console.error('[약관 v7.0 재확인] 세션 기록 실패 — 모달 표시:', e?.message || e);
        }
        setDelegationV70Open(true);
    }, [delegationV70.required, delegationV70.loading, userData?.uid]);
    useEffect(() => {
        const open = () => setDelegationV70Open(true);
        window.addEventListener(DELEGATION_V70_OPEN_EVENT, open);
        return () => window.removeEventListener(DELEGATION_V70_OPEN_EVENT, open);
    }, []);
    const onDelegationV70Confirmed = async () => {
        setDelegationV70Open(false);
        try {
            const fn = typeof refreshUserData === 'function' ? refreshUserData : window.refreshUserData;
            if (typeof fn === 'function') await fn();
            else console.error('[약관 v7.0 재확인] refreshUserData 없음 — 새로고침 전까지 잠금 표시가 남을 수 있음');
        } catch (e) {
            console.error('[약관 v7.0 재확인] 사용자 정보 갱신 실패(기록은 완료):', e?.message || e);
        }
    };

    // 📢 약관 v6.2 안내 띠 노출 대상 (위 policyV62* 참고) — 업체 계정만.
    //   제외: 개인회원(isIndividual) · 해외 기공소(labKind overseas — 이번 요금 변경 대상 아님, 한국어 요금 안내 부적합)
    //        · v6.2 공고 이후 가입 회원(가입 시 v6.2 동의 — "지금 쓰시는 회원 요금 그대로" 문구가 사실과 다름)
    //        · 환자가 볼 수 있는 화면(/reception 데스크 접수, /patients 환자 관리 — 환자 화면에 "무료" 문구 금지, 공고문안 (6))
    //   환자 계정(userType patient)은 App.js 환자 분기에서 MainLayout 자체에 오지 않는다.
    const POLICY_V62_FROM_MS = Date.parse('2026-10-04T00:00:00+09:00');
    const POLICY_V62_UNTIL_MS = Date.parse('2026-11-04T00:00:00+09:00');   // 11-03 하루 끝까지 노출
    const policyV62Audience = (() => {
        if (!userData || isIndividual) return null;
        if (userData?.labKind === 'overseas') return null;
        const nowMs = Date.now();
        if (nowMs < POLICY_V62_FROM_MS || nowMs >= POLICY_V62_UNTIL_MS) return null;
        const p = location.pathname;
        if (p.startsWith('/reception') || p.startsWith('/patients') || p.startsWith('/walkin') || p.startsWith('/p/')) return null;
        const c = userData?.createdAt;
        const createdMs = c?.toMillis ? c.toMillis() : (typeof c?.seconds === 'number' ? c.seconds * 1000 : (c ? Date.parse(c) : NaN));
        if (Number.isFinite(createdMs) && createdMs >= POLICY_V62_FROM_MS) return null;
        if (['lab', '기공소'].includes(businessType)) return 'lab';
        if (['clinic', 'dental', '치과'].includes(businessType)) return 'clinic';
        return 'common';
    })();
    // 📢 약관 v7.0 공고 띠 노출 대상 (위 policyV70* 참고) — 기공소·치과 업체 계정(직원 포함). 가입일 무관(공고~시행 사이 가입자도 시행 시 v7.0 적용 대상).
    //   제외: 개인회원 · 해외 기공소(한국어 위탁 조항 고지 대상 아님 — v6.2 와 같은 기준) · 환자가 볼 수 있는 화면(/reception·/patients·/walkin·/p/).
    //   features 로드 전(null)에는 기본 시행일 기준으로 판정(getTermsV70AtMs 폴백).
    // ⛔ 2026-10-28 대표 결정 「약관 띠는 없애줘」(증빙 위험 안내 후 「전부 제거」 선택) — v7.0·v7.1 띠는 로그인·메인 모두 그리지 않는다. /terms 공고 상자·전문은 그대로. 되돌리려면 TERMS_BANNERS_OFF=false.
    const TERMS_BANNERS_OFF = true;
    const policyV70Show = (() => {
        if (TERMS_BANNERS_OFF) return false;
        if (!userData || isIndividual) return false;
        if (userData?.labKind === 'overseas') return false;
        if (!isTermsV70NoticePeriod(features)) return false;
        const p = location.pathname;
        if (p.startsWith('/reception') || p.startsWith('/patients') || p.startsWith('/walkin') || p.startsWith('/p/')) return false;
        return ['lab', '기공소', 'clinic', 'dental', '치과'].includes(businessType);
    })();
    // 📢 약관 v7.1 공고 띠 노출 대상 — v7.0 띠와 같은 기준(기공소·치과 업체 계정, 개인·해외 기공소·환자 화면 제외). 기간만 v7.1 기준(10-24 ~ 시행 11-24 전)
    //   v8.0 공고가 시작되면(isTermsV80Announced) v7.1 띠는 숨긴다 — v7.1 공고는 v8.0 공고로 대체(초안 §1 「v7.1 공고 처리」)
    const policyV71Show = (() => {
        if (TERMS_BANNERS_OFF) return false;
        if (!userData || isIndividual) return false;
        if (userData?.labKind === 'overseas') return false;
        if (!isTermsV71NoticePeriod(features)) return false;
        if (isTermsV80Announced(features)) return false;
        const p = location.pathname;
        if (p.startsWith('/reception') || p.startsWith('/patients') || p.startsWith('/walkin') || p.startsWith('/p/')) return false;
        return ['lab', '기공소', 'clinic', 'dental', '치과'].includes(businessType);
    })();
    // 📢 약관 v8.0 공고 띠 노출 대상 — v7.0 띠와 같은 기준. 기간은 config/features.termsV80NoticeAt ~ termsV80At(값이 없으면 항상 false = 공고 전)
    const policyV80Show = (() => {
        if (!userData || isIndividual) return false;
        if (userData?.labKind === 'overseas') return false;
        if (!isTermsV80NoticePeriod(features)) return false;
        const p = location.pathname;
        if (p.startsWith('/reception') || p.startsWith('/patients') || p.startsWith('/walkin') || p.startsWith('/p/')) return false;
        return ['lab', '기공소', 'clinic', 'dental', '치과'].includes(businessType);
    })();
    // 📢 처리방침 v3.2.8 공고 띠 노출 대상 — v7.0 띠와 같은 업체 계정(기공소·치과, 직원 포함), 환자 화면 제외(문안 §3-1). 기간만 v3.2.8 기준
    const policyV328Show = (() => {
        if (!userData || isIndividual) return false;
        if (userData?.labKind === 'overseas') return false;
        if (!isPrivacyV328BannerPeriod()) return false;
        const p = location.pathname;
        if (p.startsWith('/reception') || p.startsWith('/patients') || p.startsWith('/walkin') || p.startsWith('/p/')) return false;
        return ['lab', '기공소', 'clinic', 'dental', '치과'].includes(businessType);
    })();
    // 📢 처리방침 v3.3.5 공고 띠 노출 대상 — v3.2.8 띠와 같은 조건, 기간만 v3.3.5 기준. 제조사(outsource)도 수집 항목 대상이라 포함
    const policyV335Show = (() => {
        if (!userData || isIndividual) return false;
        if (userData?.labKind === 'overseas') return false;
        if (!isPrivacyV335BannerPeriod()) return false;
        const p = location.pathname;
        if (p.startsWith('/reception') || p.startsWith('/patients') || p.startsWith('/walkin') || p.startsWith('/p/')) return false;
        return ['lab', '기공소', 'clinic', 'dental', '치과', 'outsource'].includes(businessType);
    })();
    const phaseOpen = (min) => isBeta || ((features?.phase || 1) >= min);
    // 🎯 상용화 단계 게이트 (2026-08-20 대표 확정) — 신규 게이트는 stage 사용 (구 phase는 동결)
    const stageOk = (min) => isBeta || stageOpen(min, features, userData, auth.currentUser?.email);
    // 🦷 치과 스캔 수집 노출 — clinicScanIntake 플래그 && (베타 || stage 2) (2026-08-20 대표 확정)
    const scanIntakeOn = isClinicScanIntakeOn(features, userData, auth.currentUser?.email);
    // 🦷 치과 환자관리 노출 — clinicPatients 플래그 ON 또는 베타 미리보기 (2026-09-10). 치과 슬림 메뉴 3곳 '환자 관리'
    // eslint-disable-next-line no-unused-vars -- 2026-10-30 '환자 자동관리 (준비중)' 단일 항목으로 메뉴에서는 안 쓰지만 플래그·데이터는 보존(계획_구독플랜제 §4)
    const clinicPatientsOn = isClinicPatientsOn(features, userData, auth.currentUser?.email);
    // 🔔 재내원 안내 노출 — clinicRecall 플래그 strict(베타 미리보기 없음) && 환자관리 게이트 (2026-10-01). '환자 관리' 바로 아래 3곳
    // eslint-disable-next-line no-unused-vars -- 2026-10-30 '환자 자동관리 (준비중)' 단일 항목으로 메뉴에서는 안 쓰지만 플래그·데이터는 보존(계획_구독플랜제 §4)
    const clinicRecallOn = isClinicRecallOn(features, userData, auth.currentUser?.email);
    // 🧾 접수 노출 — deskReception 플래그 strict(베타 미리보기 없음) (2026-10-03). 치과 메뉴 3곳(대표 전체·대표 슬림·직원) 맨 위 '접수' 그룹
    const deskReceptionOn = isDeskReceptionOn(features);
    // 🗺️ 진료 후 질문 배지 — roadmapEngine 플래그 strict AND 파일럿 치과(useRoadmapPilot, fail-closed) 일 때만 getRoadmapInboxCount(건수만). OFF면 호출 0. 5분마다 + /patients 진입 때 갱신
    const { on: roadmapOn } = useRoadmapPilot(userData ? { ...userData, email: userData.email || auth.currentUser?.email } : null);
    const isClinicAcctForBadge = ['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType);
    const [roadmapInboxCount, setRoadmapInboxCount] = useState(0);
    useEffect(() => {
        if (!roadmapOn || !isClinicAcctForBadge) { setRoadmapInboxCount(0); return; }
        let alive = true;
        const tick = () => getRoadmapInboxCount()
            .then(r => { if (alive) setRoadmapInboxCount(Number.isInteger(r?.toCheckCount) ? r.toCheckCount : 0); })
            .catch(e => console.error('[진료 후 질문 배지] 건수 조회 실패:', e?.code || e?.details?.code || e?.message || e));
        tick();
        const id = setInterval(tick, 5 * 60 * 1000);
        return () => { alive = false; clearInterval(id); };
    }, [roadmapOn, isClinicAcctForBadge, location.pathname.startsWith('/patients')]); // eslint-disable-line react-hooks/exhaustive-deps
    // 📅 예약 메뉴·배지 (2026-10-11, 계획_케어앱_예약대기 W2) — careBooking 플래그 strict AND 서버 available. OFF면 서버 호출 0, 실패 시 숨김(fail-closed).
    //   배지 = 새 요청 수(getClinicBookingInboxCount — 건수만). 5분마다 + /patients 진입 때 갱신(로드맵 배지와 같은 방식)
    const { on: bookingMenuOn } = useBookingAvailable(userData ? { ...userData, email: userData.email || auth.currentUser?.email } : null);
    const [bookingNewCount, setBookingNewCount] = useState(0);
    useEffect(() => {
        if (!bookingMenuOn || !isClinicAcctForBadge) { setBookingNewCount(0); return; }
        let alive = true;
        const tick = () => getClinicBookingInboxCount()
            .then(r => { if (alive) setBookingNewCount(Number.isInteger(r?.newCount) ? r.newCount : 0); })
            .catch(e => console.error('[예약 배지] 건수 조회 실패:', e?.code || e?.details?.code || e?.message || e));
        tick();
        const id = setInterval(tick, 5 * 60 * 1000);
        return () => { alive = false; clearInterval(id); };
    }, [bookingMenuOn, isClinicAcctForBadge, location.pathname.startsWith('/patients')]); // eslint-disable-line react-hooks/exhaustive-deps
    // eslint-disable-next-line no-unused-vars -- 2026-10-30 '환자 자동관리 (준비중)' 단일 항목으로 메뉴에서는 안 쓰지만 플래그·데이터는 보존(계획_구독플랜제 §4)
    const bookingMenuItem = bookingMenuOn ? [{ name: '예약', icon: CalendarCheck, path: '/patients/bookings' }] : [];
    // 2026-10-30 대표 결정(계획_구독플랜제 §4): 치과 메뉴의 '환자 자동관리'·'재내원 안내'·'예약' 대신 '환자 자동관리 (준비중)' 하나.
    //   눌러도 이동 없이 안내 alert(해외 수주 받기 준비중과 같은 방식 — handleSidebarItemClick item.soon). 라우트·플래그·데이터는 그대로.
    //   bookingMenuItem·clinicPatientsOn·clinicRecallOn 은 다른 곳(배지 등)에서 계속 쓰므로 남긴다.
    const patientsSoonItem = [{ name: '환자 자동관리 (준비중)', icon: Users, path: '/patients-coming-soon', soon: true, soonText: '환자 자동관리는 준비 중입니다.' }];
    // 그룹 객체를 스프레드로 맨 앞에 끼운다 — OFF면 키 자체가 없어 그룹 순서·기본 카테고리에 영향 없음
    // 2026-10-06 대표 지시: '환자 관리'는 '접수' 바로 아래(접수 그룹 안). 접수 OFF면 종전대로 '의뢰' 그룹에 남긴다
    const receptionGroup = deskReceptionOn ? { reception: { name: '접수 관리', icon: QrCode, items: [
        { name: '접수 관리', icon: QrCode, path: '/reception' },
        ...patientsSoonItem,   // 2026-10-30: 환자 자동관리·재내원 안내·예약 → '환자 자동관리 (준비중)' 하나
    ] } } : {};
    // 2026-08-03 대표 지시: '덴클 에이전트' 사이드메뉴는 아이반 기공소만 노출 (다운로드 페이지 /download-agent 직접 접속은 유지)
    const isIvanLab = (auth.currentUser?.email || '').toLowerCase() === 'ivanlab@naver.com';
    // 🧩 지그 생성 노출 = jigLocal 플래그 ON 또는 베타(아이반) (2026-10-19)
    const jigLocalOn = isJigLocalOn(features, userData, auth.currentUser?.email);
    // 🧩 지그 결과(a 클라우드 보관) 노출 = jigWeb 플래그 ON 또는 베타. jigLocal 과 독립 (2026-10-19)
    const jigWebOn = isJigWebOn(features, userData, auth.currentUser?.email);
    // 🧩 완성 디자인(재택·외주·지그 공통 웹보관) 노출 = designResults 플래그 ON (strict, 기본 OFF) (2026-10-20)
    // 2026-10-21 '완성 디자인' 사이드 메뉴 제거 — 플래그·판정은 보존(라우트 게이트 App.js). 메뉴 미사용이라 lint 보존 주석.
    // eslint-disable-next-line no-unused-vars -- 보존: /design-results 라우트 플래그, 메뉴 노출만 삭제
    const designResultsOn = isDesignResultsOn(features);

    // 🔔 에이전트 업데이트 필요 배지 (대표 지시 2026-08-25) — 최근 30일 하트비트 PC 중 최신 배포판보다 낮은 버전이 있으면
    // 사이드메뉴 '덴클 프로그램'에 '업데이트' 표기. 하트비트 없음(미설치)은 배지 없음 — 모르는 것을 표시하지 않는다.
    const [agentUpdateNeeded, setAgentUpdateNeeded] = useState(false);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const tid = userData?.companyId || userData?.uid;
                if (!tid) return;
                const snap = await getDoc(doc(db, 'agentStatus', tid));
                if (!alive || !snap.exists()) return;
                const devices = snap.data().devices || {};
                const cutoff = Date.now() - 30 * 86400000;
                const needs = Object.values(devices).some(d => {
                    const seen = d?.lastSeen?.toMillis ? d.lastSeen.toMillis() : 0;
                    return seen > cutoff && isAgentOutdated(d?.version);
                });
                if (alive) setAgentUpdateNeeded(needs);
            } catch (e) {
                console.warn('에이전트 버전 확인 실패(배지 생략):', e?.message);
            }
        })();
        return () => { alive = false; };
    }, [userData?.uid, userData?.companyId]);

    // 💰 치과 「이번 달 예상 기공비」 기공소 대표 1회 고지 배너 (2026-10-07 대표 결정 — 기본 켜짐의 필수 짝)
    //   대상: 국내 기공소 대표(직원 제외) · 연결(accepted) 회원 치과 1곳 이상 · labShareSettings/{uid}.expectedCostNoticeAckAt 없음.
    //   [확인] = callable ackExpectedCostNotice(멱등) → 다시 안 보임. 확인 전에는 매 로그인 노출(localStorage 닫기 없음 — 증빙은 서버 기록만).
    //   문안: docs/법무검토_치과_예상기공비_2026-10-07.md §4-3 글자 그대로. 환자가 볼 수 있는 경로(/reception·/patients·/walkin·/p/)에서는 숨김.
    const [expectedCostNotice, setExpectedCostNotice] = useState({ show: false, busy: false, error: '' });
    useEffect(() => {
        const uid = userData?.uid;
        const bt = userData?.businessType;
        const isDomesticLabOwner = !!uid && !userData?.companyId && userData?.userType !== 'staff'
            && (bt === 'lab' || bt === '기공소') && userData?.labKind !== 'overseas';
        if (!isDomesticLabOwner) { setExpectedCostNotice(n => (n.show ? { ...n, show: false } : n)); return; }
        let alive = true;
        (async () => {
            try {
                const ss = await getDoc(doc(db, 'labShareSettings', uid));
                if (!alive) return;
                if (ss.exists() && ss.data()?.expectedCostNoticeAckAt) return;   // 이미 확인
                // 연결 회원 치과 1곳 이상인지 — 3방향 where 필터, 치과 businessType 확인(최대 10곳만 조회)
                const ref = collection(db, 'connections');
                const [s1, s2, s3] = await Promise.all([
                    getDocs(query(ref, where('requesterId', '==', uid), where('status', '==', 'accepted'))),
                    getDocs(query(ref, where('receiverId', '==', uid), where('status', '==', 'accepted'))),
                    getDocs(query(ref, where('recipientId', '==', uid), where('status', '==', 'accepted'))),
                ]);
                const ids = new Set();
                [...s1.docs, ...s2.docs, ...s3.docs].forEach(d => {
                    const c = d.data();
                    const pid = c.requesterId === uid ? (c.receiverId || c.recipientId) : c.requesterId;
                    if (pid && pid !== uid) ids.add(pid);
                });
                let hasClinic = false;
                for (const pid of [...ids].slice(0, 10)) {
                    const u = await getDoc(doc(db, 'users', pid));
                    if (u.exists() && ['clinic', 'dental', '치과'].includes(u.data()?.businessType || '')) { hasClinic = true; break; }
                }
                if (alive && hasClinic) setExpectedCostNotice({ show: true, busy: false, error: '' });
            } catch (e) {
                console.warn('예상 기공비 고지 배너 판정 실패(배너 생략):', e?.message);   // 조용히 넘기지 않음 — 로그
            }
        })();
        return () => { alive = false; };
    }, [userData?.uid, userData?.companyId, userData?.businessType, userData?.userType, userData?.labKind]);
    const ackExpectedCostNotice = async () => {
        if (expectedCostNotice.busy) return;
        setExpectedCostNotice(n => ({ ...n, busy: true, error: '' }));
        try {
            await httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'ackExpectedCostNotice')();
            setExpectedCostNotice({ show: false, busy: false, error: '' });
        } catch (e) {
            console.error('예상 기공비 고지 확인 기록 실패:', e);
            const code = e?.code || '';
            setExpectedCostNotice(n => ({ ...n, busy: false, error: code.includes('not-found')
                ? '확인 기록 기능이 아직 서버에 준비되지 않았습니다. 잠시 후 다시 눌러 주세요.'
                : `확인을 기록하지 못했습니다: ${e?.message || e}` }));
        }
    };
    const expectedCostNoticeShow = expectedCostNotice.show && !['/reception', '/patients', '/walkin', '/p/'].some(p => location.pathname.startsWith(p));
    // 「기간 예상 기공료」 오픈 예정일 — config/features.clinicExpectedStatementOpenAt (없으면 "곧"). 배너·카톡·플래그 ON 날짜 3곳 동일해야 함(법무 §6)
    const expStmtOpenAt = getClinicExpectedStatementOpenAtText(features);

    // PIN 정보 로드
    useEffect(() => {
        const loadUserPin = async () => {
            if (userData?.uid) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', userData.uid));
                    if (userDoc.exists()) {
                        const d0 = userDoc.data();
                        setUserPin(d0.pinSet === true || !!d0.pin ? 'set' : null);
                    }
                } catch (e) {
                    console.error('PIN 로드 실패:', e);
                }
            }
        };
        loadUserPin();
    }, [userData?.uid]);

    // 미구독 사용자 라우트 보호 (2026-07 유료 전환)
    // - 미구독 국내 기공소: 유료 기능 경로 진입 시 /subscription으로 유도
    // - 치과: 무료 정책 유지 (가드 제외)
    useEffect(() => {
        if (!userData || isAdmin || isSubscribed) return;
        // 전면 무료 (대표 확정 2026-08-24): 미구독 리다이렉트 가드 전체 해제 — 유료화 재개 = allFree=false
        //   W4(계획_가입승인결제흐름): 판정을 paywallActive 한 곳으로 (= allFree !== true)
        if (!paywallActive(features)) return;
        // 접근 모드(App.js computeAccessMode) — full 이면 가드 없음. 모드가 없으면(옛 userData) 종전 판정으로 진행
        const accessMode = userData?.accessMode;
        if (accessMode === 'full') return;
        // 해외 기공소는 구독 대상 아님 (수수료 모델) — 가드 비활성화, /global/* 자유 접근
        if (userData?.labKind === 'overseas') {
            if (!location.pathname.startsWith('/global') && !['/chat', '/settings', '/profile', '/connections', '/lab/'].some(p => location.pathname.startsWith(p))) {
                navigate('/global/status');
            }
            return;
        }
        // 치과 구독 가드 (2026-08-17 대표 확정, 약관 v6.0):
        // - config/features.clinicSubscription OFF(기본·features 미로드 포함) = 현행 그대로 전면 면제 → 배포해도 화면 변화 0
        // - ON = 치과도 가드 대상. 단 users.subscriptionExempt===true(서버가 심는 필드 — 아이반 기공소
        //   [76Y6wwTXUZcN7lbiHds3ZYSQcK92] 거래연결 치과 영구 면제)는 계속 면제
        const businessType = userData?.businessType || userData?.companyBusinessType; // 치과 직원(companyBusinessType) 포함 (2026-07-28)
        if (businessType === 'clinic' || businessType === 'dental' || businessType === '치과') {
            // clinic_locked(v7.1 요금표 치과 — 2단계 termsV71At 이후에만 생김)만 허용 목록 가드를 건다.
            //   의뢰서 작성·보낸 의뢰서는 허용 목록에 들어 있다(대표 10-15: 모든 치과 무료). v6.2 치과(clinic_v62_locked)는 아래 종전 규칙.
            if (accessMode === 'clinic_locked') {
                if (location.pathname !== '/' && !isPathAllowedForMode('clinic_locked', location.pathname)) navigate('/subscription');
                return;
            }
            // 치과는 리다이렉트 가드 대상이 아니다 (2026-08-21 대표 지시: "거래처 관리와 의뢰서 작성은
            // 열어줘야해 기본은 무료거든") — 잠금은 8개 화면의 ClinicSubGate(보이는 잠금 카드)가 전담하고,
            // 의뢰서 작성·보낸 의뢰서·거래처 관리 등 나머지는 구독 여부와 무관하게 접근 가능해야 한다.
            // 기공소용 freePaths 리다이렉트를 치과에 적용하면 /create-order·/connections에서 튕기는 사고.
            return;
        }
        // 미구독 리다이렉트는 국내 기공소(대표·직원)만 — 밀링센터(outsource)·판매자(seller)·개인 등 구독 대상이 아닌 유형이
        // allFree 해제 시 /subscription으로 잠기는 것 방지 (계획 W3, 요금 판정표 §3 2번 줄)
        // (businessType 필드가 없는 옛 기공소 계정은 userType 'lab'으로 판정 — 기존 가드 범위 축소 방지)
        const isDomesticLab = businessType === 'lab' || (!businessType && userData?.userType === 'lab');
        if (!isDomesticLab) return;
        const path = location.pathname;
        // W4: 결제 전 기공소 = 제한 모드 허용 목록(utils/billingPolicy.js ACCESS_ALLOW.lab_limited 한 곳)으로 판정.
        //   받은 의뢰서(상세·인쇄·첨부)·계정·홍보·거래처·치과 초대·기존 명세서·계산서 읽기 전용·결제·계정 삭제·약관은 열림, 그 밖 → /subscription
        //   종전 freePaths(2026-10-04): '/find-lab','/lab-advertising','/upgrade','/profile','/subscription','/billing','/settings','/signup','/login',
        //   '/promo','/lab','/review','/feed','/portfolio','/staff-join','/staff-invitations','/dashboard','/clinic/','/clinic-invites','/account/delete'
        //   — 의뢰서·거래처를 잠가 법무 P1(의뢰서 전달 실패) 위반이라 교체. 대시보드·/clinic/ 등은 제한 모드에서 잠긴다(계획 §2)
        if (path !== '/' && !isPathAllowedForMode('lab_limited', path)) navigate('/subscription');
    }, [location.pathname, isSubscribed, isAdmin, userData, features]); // eslint-disable-line react-hooks/exhaustive-deps

    // 알림 권한 확인 - 처음 로그인 시
    useEffect(() => {
        let timeoutId;
        if (userData?.uid) {
            const status = typeof Notification !== 'undefined' ? Notification.permission : 'not_supported';
            const hasAsked = localStorage.getItem('notificationAsked_' + userData.uid);
            if (!hasAsked && status === 'default') {
                timeoutId = setTimeout(() => setShowNotificationModal(true), 5000);
            }
        }
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [userData?.uid]);

    // FCM 포그라운드 메시지 리스너 초기화
    useEffect(() => {
        const unsubscribe = setupForegroundListener();
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);



    // 채팅 읽지않은 메시지 수 구독
    useEffect(() => {
        const chatUid = userData?.companyId || userData?.uid;
        if (!chatUid) return;
        const q = query(
            collection(db, 'chatRooms'),
            where('participants', 'array-contains', chatUid)
        );
        const unsub = onSnapshot(q, (snap) => {
            let total = 0;
            snap.docs.forEach(d => {
                const data = d.data();
                // 내부(자체) 채팅방은 항상 포함
                if (data.type === 'internal') {
                    total += data.unreadCount?.[chatUid] || 0;
                    return;
                }
                // 일반 채팅방: participantInfo 없는 방 제외
                const others = (data.participants || []).filter(p => p !== chatUid);
                if (others.length === 0) return;
                const otherInfo = data.participantInfo?.[others[0]];
                if (!otherInfo || (!otherInfo.companyName && !otherInfo.name)) return;
                total += data.unreadCount?.[chatUid] || 0;
            });
            setChatUnreadCount(total);
        }, () => {});
        return () => unsub();
    }, [userData?.companyId, userData?.uid]);

    const handleCloseNotificationModal = () => {
        setShowNotificationModal(false);
        if (userData?.uid) {
            localStorage.setItem('notificationAsked_' + userData.uid, 'true');
        }
    };

    // 경영관리 그룹 접근 시 PIN 필요 여부 확인
    const needsPinForManagement = () => {
        // 결제사 심사용 계정(config/features.pgReviewUserIds)은 PIN 없이 설정·구독 화면 진입 (2026-09-22 대표 지시, 토스 가맹 심사)
        if (isPgReviewer(features, userData?.uid)) return false;
        return isBusinessOwner && !pinAuthenticated && !isSeller && !isAdmin;
    };

    // PIN 모달 열기 (PIN 미설정 시 안내 모달부터 표시)
    // 2026-07-30: 평문(users.pin)은 서버로 이전됨 — 존재 여부는 pinSet 플래그로 판단
    const openPinModal = async () => {
        let hasPin = !!userPin;
        try {
            if (userData?.uid) {
                const userDoc = await getDoc(doc(db, 'users', userData.uid));
                if (userDoc.exists()) {
                    const d0 = userDoc.data();
                    hasPin = d0.pinSet === true || !!d0.pin;
                    setUserPin(hasPin ? 'set' : null);
                }
            }
        } catch (e) {
            console.error('PIN 확인 실패:', e);
        }

        // PIN이 설정되지 않은 경우 → 입력창 대신 "PIN 설정 안내" 모달 표시
        if (!hasPin) {
            setShowPinSetupRequiredModal(true);
            return;
        }

        // PIN이 설정된 경우 → 기존 입력 모달
        setPinInput('');
        setPinError('');
        setPinResetMode('');
        setShowPinModal(true);
    };

    // 대표 구역 수동 잠그기 (2026-07-30 회의: 타이머 없음 — 이 버튼 + 브라우저 종료가 잠금 수단)
    const lockOwnerZone = () => {
        setPinAuthenticated(false);
        setIsMobileMenuOpen(false);
    };

    // 대표 구역 경로 목록 — URL 직접 입력 가드용 (기공소 대표만, 치과는 현행 유지 — 대표 결정 대기)
    // 2026-10-21 대표 재확정: 설정도 정산과 함께 PIN 묶음('owner') — '/settings' 가드 복원(구독 해지 URL 직접입력 방지). lab 전용 가드라 치과 무영향.
    const OWNER_ZONE_PREFIXES = ['/inbox', '/partners', '/transactions', '/tax-invoices', '/revenue', '/management-analysis', '/business-analysis', '/settings'];
    const ownerZoneBlocked = userData?.businessType === 'lab'
        && OWNER_ZONE_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
        && needsPinForManagement();

    // PIN 설정 안내 모달 닫고 프로필로 이동 (돌아올 경로 유지)
    const goToProfileForPinSetup = () => {
        setShowPinSetupRequiredModal(false);
        // pendingNavigationPath는 유지 → PIN 설정 후 다시 설정 메뉴 클릭 시 바로 이동
        navigate('/profile');
    };

    // PIN 검증 (Firestore에서 최신 PIN을 직접 조회)
    const verifyPin = async () => {
        // 2026-07-30: 검증은 서버(Cloud Function)에서 — 브라우저는 정답을 모른다.
        // 구 4자리 평문 사용자는 첫 성공 시 서버가 소리 없이 해시로 이전((나)안).
        if (pinVerifying) return;
        setPinVerifying(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            await httpsCallable(fns, 'verifyOwnerPin')({ pin: pinInput });
            setPinAuthenticated(true);
            setShowPinModal(false);
            setPinInput('');
            setPinError('');
            // PIN 인증 성공 → 원래 가려던 페이지로 이동
            if (pendingNavigationPath) {
                navigate(pendingNavigationPath);
                setPendingNavigationPath(null);
                setIsMobileMenuOpen(false);
            }
        } catch (e) {
            // 서버가 남은 시도 횟수·잠금 시간을 메시지로 내려줌 (실패는 잠금 유지 — fail-closed)
            if (e?.code === 'functions/not-found') {
                setShowPinModal(false);
                setShowPinSetupRequiredModal(true);
            } else {
                setPinError(e?.message || '오류가 발생했습니다. 다시 시도해주세요.');
            }
            setPinInput('');
        } finally {
            setPinVerifying(false);
        }
    };

    // 핀 분실 — 가입 이메일로 인증코드 발송 후 재설정 모드로 전환 (비밀번호 찾기와 동일 신뢰선)
    const startPinReset = async () => {
        if (pinVerifying) return;
        setPinVerifying(true);
        setPinError('');
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            await httpsCallable(fns, 'requestPinResetCode')();
            setPinResetMode('code');
            setPinResetCode('');
            setPinResetNew('');
            setPinError('');
        } catch (e) {
            setPinError(e?.message || '인증코드 발송에 실패했습니다.');
        } finally {
            setPinVerifying(false);
        }
    };

    const submitPinReset = async () => {
        if (pinVerifying) return;
        setPinVerifying(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            await httpsCallable(fns, 'resetPinWithCode')({ code: pinResetCode, newPin: pinResetNew });
            // 이메일 소유 증명 완료 = 본인 — 새 핀 설정과 동시에 해제
            setPinAuthenticated(true);
            setShowPinModal(false);
            setPinResetMode('');
            setPinInput('');
            setPinError('');
            setUserPin('set');
            if (pendingNavigationPath) {
                navigate(pendingNavigationPath);
                setPendingNavigationPath(null);
                setIsMobileMenuOpen(false);
            }
        } catch (e) {
            setPinError(e?.message || '재설정에 실패했습니다.');
        } finally {
            setPinVerifying(false);
        }
    };

    // PIN 모달 닫기
    const closePinModal = () => {
        setShowPinModal(false);
        setPinInput('');
        setPinError('');
        setPinResetMode('');
    };

    // PIN 입력 키 이벤트 (4~6자리)
    const handlePinKeyPress = (e) => {
        if (e.key === 'Enter' && pinInput.length >= 4) verifyPin();
        else if (e.key === 'Escape') closePinModal();
    };

    // 카테고리 클릭 핸들러 (기존 아이콘 사이드바용)
    const handleCategoryClick = async (key, category) => {
        if (activeCategory === key) return;
        if (key === 'settings' && needsPinForManagement()) {
            setPendingNavigationPath(category.items[0].path);
            await openPinModal();
            return;
        }
        navigate(category.items[0].path);
    };

    // 새 사이드바 메뉴 아이템 클릭 핸들러
    // 2026-07-30: 'owner' = 기공소 대표 전용 구역. settlement/business/settings는 치과·셀러 등 다른 메뉴 체계용으로 유지.
    // 2026-10-21 PIN 범위 축소(대표 확정)는 전역 배열이 아니라 "lab 변형에서 설정·덴클 프로그램·지그를 'owner' 밖(비PIN 키 'tools')으로 이동"으로 달성 —
    //   치과·셀러의 settings/settlement PIN은 그대로 보존(배열 원복). lab의 'owner'에는 정산 3종(거래명세서·예상 매출·거래처)만 남김.
    const pinProtectedGroups = ['owner', 'settlement', 'business', 'settings'];
    const handleSidebarItemClick = async (groupKey, item) => {
        // 준비중 메뉴 — 이동하지 않고 안내만 (2026-08-15)
        if (item.soon) {
            alert(item.soonText || '해외 수주 받기는 준비 중입니다. 곧 오픈 예정입니다.');   // soonText: 항목별 안내(환자 자동관리 준비중, 2026-10-30)
            setIsMobileMenuOpen(false);
            return;
        }
        if (pinProtectedGroups.includes(groupKey) && needsPinForManagement()) {
            setPendingNavigationPath(item.path);
            await openPinModal();
            return;
        }
        navigate(item.path);
        setIsMobileMenuOpen(false);
    };

    const handleLogout = async () => {
        if (window.confirm('로그아웃 하시겠습니까?')) {
            try { await disableNotifications(userData?.uid); } catch (e) { console.log('알림 비활성화 실패:', e); }
            await signOut(auth);
            navigate('/login');
        }
    };

    // 커뮤니티 항목 참조 (모듈 레벨에서 정의된 communityItemsData 사용)
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const communityItems = communityItemsData;

    // 개인회원 메뉴
    const individualMenus = {
        // 직원가입 임시 숨김 (2026-07-19) — 오픈 시기 미정, 되살리려면 주석 해제
        // staff: {
        //     name: '직원',
        //     icon: UserPlus,
        //     items: [
        //         { name: '직원신청', icon: UserPlus, path: '/staff-join' },
        //         { name: '받은 초대', icon: Mail, path: '/staff-invitations' },
        //     ]
        // },
        // market: {
        //     name: '마켓',
        //     icon: ShoppingBag,
        //     items: [
        //         { name: '마켓', icon: ShoppingBag, path: '/marketplace' },
        //     ]
        // },
        // community: {
        //     name: '커뮤니티',
        //     icon: Globe,
        //     items: [
        //         { name: '공감마당', icon: Handshake, path: '/empathy-board' },
        //         { name: '커뮤니티', icon: Globe, path: '/community' },
        //     ]
        // },
        settings: {
            name: '설정',
            icon: Settings,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };

    // ── 치과/기공소 공통 핵심 업무 아이템 ──
    const coreItems = [
        { name: '대시보드', icon: LayoutDashboard, path: '/dashboard' },
        { name: '받은 의뢰서', icon: Package, path: '/orders' },
        { name: '기공 현황', icon: Factory, path: '/production' },   // 공정 보드 웹 이식 (2026-08-05)
        // { name: '외주', icon: Link2, path: '/outsource-order' },   // 10-30 대표: 옛 외주 화면 삭제(외주 의뢰서로 일원화) — 라우트도 삭제
        { name: '일정', icon: Calendar, path: '/schedule' },
        // 채팅 숨김 (대표 지시 2026-08-11) — 기능·라우트는 그대로, 이 줄만 풀면 복원
        // { name: '채팅', icon: MessageSquare, path: '/chat' },
        { name: '재제작 분석', icon: RotateCcw, path: '/remake-analysis' },
    ];

    // ── 치과 대표 메뉴 ──
    const clinicMenus = {
        ...receptionGroup,   // 🧾 접수 — 맨 위 (2026-10-03 대표 지시)
        // 환자 그룹 임시 숨김 (아직 미활용) — 되살리려면 아래 주석 해제
        // patient: {
        //     name: '환자', icon: Users,
        //     items: [
        //         { name: '분석 대시보드', icon: BarChart3, path: '/clinic/analytics' },
        //         { name: '치과앱 환자', icon: Users, path: '/clinic/my-patients' },
        //         { name: '환자 알림 및 예약', icon: Bell, path: '/clinic/patient-notify' },
        //         { name: '의료기록 전송', icon: Send, path: '/clinic/send-record' },
        //         { name: '공지사항 작성', icon: Megaphone, path: '/clinic/offers' },
        //         { name: '홍보페이지 작성', icon: Megaphone, path: '/clinic/patient-app-promo' },
        //         { name: '운영 정보 관리', icon: Sparkles, path: '/clinic/profile-completeness' },
        //     ]
        // },
        // 2026-07-19 메뉴 개편: 진료·주문 / 거래 확인 / 기공소 / 자동화 / 설정
        care: {
            name: '기공 관리', icon: FileText,   // 2026-10-06 대표 지시: '의뢰' → '기공 관리'
            items: [
                { name: '기공 캘린더', icon: LayoutDashboard, path: '/clinic-today' },   // 2026-10-07 대표 지시: '오늘' → '기공 캘린더'
                { name: '의뢰서 작성', icon: FilePlus, path: '/create-order' },
                { name: '보낸 의뢰서', icon: Package, path: '/orders' },
                ...(!deskReceptionOn ? patientsSoonItem : []),   // 2026-10-30: 환자 자동관리·재내원 안내·예약 → '환자 자동관리 (준비중)' 하나. 접수 ON이면 접수 그룹에
                // 제작 현황 — 치과에서는 「오늘」에 통합 (대표 확정 2026-08-11). 기공소 메뉴에는 그대로 있음
                // { name: '제작 현황', icon: Factory, path: '/production' },
                // 스캔 수집: 플래그 ON && (베타 || stage 2) — 판정은 scanIntakeOn 헬퍼 하나로 통일 (2026-08-20, 구 phaseOpen(3) 제거)
                ...(scanIntakeOn ? [{ name: '최근 스캔', icon: ScanLine, path: '/clinic/scans' }] : []),
                // 2026-10-06 대표 지시: '원장 메뉴'(기공비·재제작 분석)·'기공소'(A/S) 그룹을 없애고 항목을 기공 관리 맨 아래로. 잠금은 라우트 SensitiveGate 그대로
                { name: '기공비 분석', icon: Wallet, path: '/lab-cost' },
                { name: '재제작 분석', icon: RotateCcw, path: '/remake-analysis' },
                ...(phaseOpen(4) ? [{ name: 'A/S 재제작 상담', icon: ClipboardList, path: '/as-remake' }] : []),
            ],
        },
        // 치과는 수신 입장 — 받은 명세서·계산서를 확인하고 수정 요청하는 화면
        billing: {
            name: '받은 서류', icon: FileSpreadsheet,
            items: [
                { name: '받은 명세서', icon: FileSpreadsheet, path: '/transactions' },   // 대표 10-15 다시 표시
                // { name: '받은 계산서 (준비 중)', icon: FileText, path: '/tax-invoices' },   // 준비 중 표기 — 대표 지시 2026-10-04   // 숨김 — 대표 10-15 "나중에 작업할 때 다시" (라우트도 App.js 에서 막음)
                // { name: '정산 확인 (준비 중)', icon: ClipboardList, path: '/clinic/settlement' },   // 숨김 — 대표 10-15 "나중에 작업할 때 다시" (라우트도 App.js 에서 막음)
            ],
        },
        // 원장 전용 분석 (2026-08-09 대표 승인) — 베타 치과.
        // groupKey는 'owner'가 아니라 'analysis': 'owner'로 두면 핀 미설정 치과가 "핀부터 설정" 안내에 막혀
        // 화면 자체를 못 연다. 잠금은 라우트의 SensitiveGate가 담당 — 핀이 있으면 묻고, 없으면 그냥 열린다.
        // 2026-10-06 대표 지시: '원장 메뉴'·'기공소' 그룹 제거 — 항목은 위 care(기공 관리) 맨 아래로 이동. 기공소 찾기 주석·보철 갤러리 주석은 이력용으로 보존
        //   // { name: '기공소 찾기', icon: Search, path: '/clinic/find-lab' }  (2026-08-15 숨김)
        //   // { name: '보철 갤러리', icon: Camera, path: '/feed' }
        automation: {
            name: '연결 프로그램', icon: HardDriveDownload,
            items: [
                // 덴클 프로그램 노출 (대표 승인 2026-08-21 "덴클프로그램도 잠금으로 나오게해줘"):
                // 구독 스위치 ON이면 잠금 상태로 노출(눌렀을 때 ClinicSubGate 구독 카드) — OFF일 땐 종전대로
                // 숨김(8/8 정책: OFF 상태 노출 = 잠금 없이 열리는 사고). 스위치가 노출·잠금을 동시에 켠다.
                // 치과 사이드메뉴에서 덴클 프로그램 제거 (대표 지시 2026-10-04) — /download-agent 라우트는 유지. 복원 시 아래 주석 해제
                // ...((scanIntakeOn || isClinicSubscriptionOn(features) || isAllFree(features)) ? [{ name: '덴클 프로그램', icon: HardDriveDownload, path: '/download-agent' }] : []),
            ],
        },
        settings: {
            name: '설정', icon: Settings,
            items: [
                { name: '설정', icon: Settings, path: '/settings' },
            ]
        },
    };

    // ── 기공소 1차(거래명세서 서비스) 슬림 메뉴 — 비베타 기공소 전용 (2026-07-23 사용자 확정) ──
    // 거래명세서 중심으로 먼저 출시: 명세서/계산서/에이전트/설정만 노출, 나머지는 베타(아이반)만
    // 2026-07-30 회의 확정: 일반(직원 OK) / 🔒owner(대표 전용, 핀 1회 해제) 2구역 재편
    // 2026-08-18 대표 확정 재편: 치과연결(케이스 들어오는 곳) / 외주·재택(일감 주고받기) / 청구(본체, 핀 유지) / 연결·설정
    //   — 용도 재정의 회의(docs/회의록_2026-08-18_덴클_용도재정의.md "본체+입구 3개") 반영. 항목 추가·삭제 없음, 묶음·이름만.
    //   '외주 의뢰서' 개명·목록 착지는 #38 구현 시 (그 전엔 'CAD 디자인 외주' 유지)
    // 2026-10-21 사이드바 재정리(대표 확정): 4묶음 — 기공 업무 / 자동 제작·디자인 / 정산(🔒PIN) / 도구·설정.
    //   PIN은 '정산'(owner 키) 묶음만. 설정·덴클 프로그램·지그·외주·재택은 PIN 없이 접근. 항목 추가·삭제 없음(재배치만).
    const labPhase1Menus = {
        work: {
            name: '기공 업무', icon: FileText,
            items: [
                // 받은 의뢰서 — 기본 개방 (2026-08-20 대표 확정: clinicInvite 조건 제거, 상시 노출)
                { name: '받은 의뢰서', icon: Package, path: '/orders' },
                // 제작 현황 보드 — 일반 구역 (직원·기술자가 밀링 체크, 2026-08-05 대표 확정)
                { name: '기공 현황', icon: Factory, path: '/production' },
                // 재제작 분석 — 그룹 맨 아래 (대표 지시 2026-08-10)
                { name: '재제작 분석', icon: RotateCcw, path: '/remake-analysis' },
            ],
        },
        make: {
            name: '자동 제작·디자인', icon: Zap,
            // 🎯 상용화 단계 게이트: 재택 디자이너=stage 1. 외주 의뢰서는 stage 무관(연결형 전환 P0, 10-15 대표). 베타는 stageOk 항상 통과.
            items: [
                // 🧩 지그 생성 — 플래그 ON 또는 베타(아이반). 웹은 환자정보 없는 안내 + 로컬 뷰어 열기 (2026-10-19)
                ...((jigLocalOn || jigWebOn) ? [{ name: '지그·3D 모델', icon: Factory, path: '/jig' }] : []),   // 외주 의뢰(PC)는 외주 의뢰서 「새 의뢰」 안으로(10-30)
                // 재택 작업 관제 (2026-08-09) — 파일 왕복은 덴클 프로그램, 여기선 진행 상태만. '재택근무'→'재택 디자이너' 개명 (CMO 권고, 2026-08-18)
                // '재택 디자이너' 삭제(10-30 대표 "재택 디자이너도 없애줘")
                // 📝 외주 의뢰서 (#38 구현 2026-08-18) — 종전 'CAD 디자인 외주' 개명, 전용 목록(/outsource-request) 착지. 재택 디자이너 아래로 이동(2026-10-21 대표)
                // 연결형 전환 P0(10-15 대표 Q6): 외주 의뢰서는 stage 무관하게 모든 기공소에 노출(라우트 App.js 와 같은 기준). 종전:
                // ...((stageOk(3) || isOutsourceMfOn(features, userData, auth.currentUser?.email)) ? [{ name: '외주 의뢰서', icon: Zap, path: '/outsource-request' }] : []),
                { name: '외주 의뢰서', icon: Zap, path: '/outsource-request' },
                // 'CAD 디자인 외주' 메뉴 제거 — 외주 의뢰서 › 의뢰 종류 「CAD 디자인」 안으로 이동. /lab-outsource 는 리다이렉트로 유지
                // 🖥️ 덴클 프로그램(에이전트) — 3.3만원 상품의 핵심(exocad→명세서 자동완성). 비PIN(2026-10-21 대표: make 그룹으로 이동)
                { name: '덴클 프로그램', icon: HardDriveDownload, path: '/download-agent' },
            ],
        },
        owner: {
            name: '정산·설정', icon: Lock,   // 🔒 PIN 잠금 — 정산 + 설정(구독 해지 PIN 보호, 2026-10-21 대표 재확정)
            items: [
                { name: '거래명세서', icon: FileSpreadsheet, path: '/transactions' },
                // { name: '세금계산서 (준비 중)', icon: FileText, path: '/tax-invoices' },   // 숨김 — 대표 10-15 (기공소 메뉴만, 라우트는 명세서 → 계산서 연결 때문에 유지)
                { name: '예상 매출', icon: TrendingUp, path: '/revenue' },
                // 🔎 누락 청구 감지 메뉴 제거 (대표 지시 2026-09-06) — 라우트 /missing-claims는 안전판으로 유지, 플래그 missingClaims=false
                // 2026-07-30 수집함 통합: 메뉴 숨김 — 기능은 거래명세서 작성 화면으로 이전, /inbox 라우트는 안전판 유지
                { name: '거래처', icon: Users, path: '/partners' },
                // '완성 디자인'(/design-results) 메뉴 제거 (2026-10-21 대표) — 라우트·플래그 designResultsOn 로직은 보존, 메뉴 노출만 삭제
                { name: '설정', icon: Settings, path: '/settings' },
            ],
        },
    };

    // ── 결제 전 기공소(제한 모드 lab_limited) 메뉴 — 계획_가입승인결제흐름 W4 (대표 승인 10-15) ──
    //   허용 목록(utils/billingPolicy.js ACCESS_ALLOW.lab_limited)에 있는 경로만 둔다. 거래명세서는 보기 전용(W6).
    //   받은 의뢰서는 결제와 무관하게 상세·인쇄·첨부까지 열림(법무 §3 조건 1, 대표 10-15 추가 결정)
    const labLimitedMenus = {
        work: {
            name: '기공 업무', icon: FileText,
            items: [
                { name: '받은 의뢰서', icon: Package, path: '/orders' },
                { name: '외주 의뢰서', icon: Zap, path: '/outsource-request' },   // 구독 없이 열림(대표 10-30)
            ],
        },
        owner: {
            name: '정산·설정', icon: Lock,   // 🔒 PIN 잠금 — 거래명세서·거래처·설정 (2026-10-21 대표 재확정, 설정 PIN 보호)
            items: [
                { name: '거래명세서 (보기)', icon: FileSpreadsheet, path: '/transactions' },
                { name: '거래처', icon: Users, path: '/partners' },
                { name: '설정', icon: Settings, path: '/settings' },
            ],
        },
        tools: {
            name: '업그레이드', icon: Zap,   // PIN 없이 접근 — 미구독이라 구독 CTA는 열려 있어야 함 (2026-10-21)
            items: [
                { name: '구독하고 전체 열기', icon: Zap, path: '/subscription', highlight: true },
            ],
        },
    };
    // 제한 모드 기공소 직원 — 직원은 거래처·명세서 라우트가 막혀 있으므로 받은 의뢰서·설정만
    const labLimitedStaffMenus = {
        core: {
            name: '작업', icon: FileText,
            items: [{ name: '받은 의뢰서', icon: Package, path: '/orders' }, { name: '외주 의뢰서', icon: Zap, path: '/outsource-request' }],   // 외주 의뢰서 구독 없이(10-30)
        },
        settings: {
            name: '설정', icon: Settings,
            items: [{ name: '설정', icon: Settings, path: '/settings' }],
        },
    };

    // ── 기공소 대표 메뉴 ──
    // 2026-07-19 메뉴 개편: 핵심 업무 / 자동화 / 성장 / 설정
    // 리메이크분석·통계·매칭수수료는 경영분석 안의 탭으로 통합
    // 2026-08-18 대표 확정 재편(비베타와 같은 원칙): 치과연결 / 외주·재택 / 성장 / 청구 🔒 / 연결·설정
    //   — 회의 #39 "본체+입구 3개". 항목 추가·삭제 없음(대시보드 등 베타 전용 항목 유지), 묶음·이름만.
    // 2026-10-21 사이드바 재정리(대표 확정): 비베타와 같은 4묶음 원칙 — 기공 업무 / 자동 제작·디자인 / 정산(🔒PIN) / 도구·설정.
    //   PIN은 '정산'(owner 키)만. 성장(해외 수주·내 기공소 소개) 항목은 도구·설정으로 흡수. 항목 추가·삭제 없음(재배치만).
    const labMenus = {
        work: {
            name: '기공 업무', icon: FileText,
            // 컨시어지 MVP 집중: 외주·일정 임시 숨김 (coreItems 필터)
            items: [
                ...coreItems.filter(i => !['외주', '일정', '재제작 분석', '수집함'].includes(i.name)),
                // 재제작 분석 — 그룹 맨 아래 (대표 지시 2026-08-10)
                { name: '재제작 분석', icon: RotateCcw, path: '/remake-analysis' },
            ],
        },
        make: {
            name: '자동 제작·디자인', icon: Zap,
            items: [
                // 🧩 지그 생성 — 플래그 ON 또는 베타(아이반). 웹은 환자정보 없는 안내 + 로컬 뷰어 열기 (2026-10-19)
                ...((jigLocalOn || jigWebOn) ? [{ name: '지그·3D 모델', icon: Factory, path: '/jig' }] : []),   // 외주 의뢰(PC)는 외주 의뢰서 「새 의뢰」 안으로(10-30)
                // '재택근무'→'재택 디자이너' 개명 (CMO 권고, 회의 2026-08-18)
                // '재택 디자이너' 삭제(10-30 대표)
                // 📝 외주 의뢰서 (#38 구현 2026-08-18) — 종전 'CAD 디자인 외주' 개명, 전용 목록(/outsource-request) 착지. 재택 디자이너 아래로 이동(2026-10-21 대표)
                { name: '외주 의뢰서', icon: Zap, path: '/outsource-request' },
                // 'CAD 디자인 외주' 메뉴 제거 — 외주 의뢰서 › 의뢰 종류 「CAD 디자인」 안으로 이동. // { name: 'CAD 디자인 외주', icon: Search, path: '/lab-outsource' },
                // 기공소 수집 에이전트 = 자동 거래명세서의 핵심 (2026-08-03 대표 지시: 아이반만 노출). 비PIN(2026-10-21 대표: make 그룹으로 이동)
                ...(isIvanLab ? [{ name: '덴클 프로그램', icon: HardDriveDownload, path: '/download-agent' }] : []),
            ],
        },
        owner: {
            name: '정산·설정', icon: Lock,   // 🔒 PIN 잠금 — 정산 + 설정(구독 해지 PIN 보호, 2026-10-21 대표 재확정)
            items: [
                // 2026-07-30 수집함 통합: 메뉴 숨김 — 기능은 거래명세서 작성 화면(요약줄·복원·수집 취소)으로 이전, /inbox 라우트는 안전판으로 유지
                { name: '거래명세서', icon: FileSpreadsheet, path: '/transactions' },
                // { name: '세금계산서 (준비 중)', icon: FileText, path: '/tax-invoices' },   // 숨김 — 대표 10-15 (기공소 메뉴만, 라우트는 명세서 → 계산서 연결 때문에 유지)
                { name: '예상 매출', icon: TrendingUp, path: '/revenue' },
                // 🔎 누락 청구 감지 메뉴 제거 (대표 지시 2026-09-06) — 라우트 /missing-claims는 안전판으로 유지, 플래그 missingClaims=false
                { name: '거래처', icon: Users, path: '/partners' },
                // '완성 디자인'(/design-results) 메뉴 제거 (2026-10-21 대표) — 라우트·플래그 designResultsOn 로직은 보존, 메뉴 노출만 삭제
                { name: '설정', icon: Settings, path: '/settings' },
                // 사이드메뉴 제외 (2026-07-25 사장 지시) — 페이지는 유지, 필요 시 주석 해제
                // { name: '필기의뢰서 양식', icon: PenLine, path: '/handwritten-template' },
            ]
        },
        tools: {
            name: '성장', icon: TrendingUp,   // 비PIN 키('tools') — 해외 수주·내 기공소 소개 (2026-10-21)
            items: [
                // 글로벌 수주 — 라우트·기능은 그대로. 지금은 '해외 수주 받기 · 준비중'으로 노출 (대표 지시 2026-08-15).
                //   오픈 시 soon 제거하고 phaseOpen(2) 게이트로 실제 /global-lab 연결.
                { name: '해외 수주 받기', icon: Globe, path: '/global-lab', soon: true },
                ...(phaseOpen(4) ? [
                    // 테스트보철 게시판 — 기공소 메뉴에서 숨김 (대표 지시 2026-08-15). 라우트·기능은 유지, 필요 시 주석 해제
                    // { name: '테스트보철 게시판', icon: ClipboardList, path: '/test-cases' },
                    { name: '내 기공소 소개', icon: Megaphone, path: '/lab-advertising' },
                ] : []),
                // ── 임시 숨김 (경영분석·긴급외주 등, 2026-07-14/25 사장 지시) — 되살리려면 주석 해제 ──
                // { name: '경영분석', icon: BarChart3, path: '/business-analysis' },
            ]
        },
    };

    // ── 치과 무료 메뉴 ──
    // 순서: 업그레이드(마케팅 신청) 먼저 → 환자 관리 → 기공소 → 잠금 → 설정
    // eslint-disable-next-line no-unused-vars -- 보존(미사용) 코드, 요금 변경 작업 범위 밖
    const clinicFreeMenus = {
        upgrade: {
            name: '업그레이드', icon: Zap,
            items: [
                { name: '마케팅 신청하기', icon: Zap, path: '/subscription', highlight: true },
            ]
        },
        // 환자 관리 — 구 환자앱 폐기 09-14: 구 환자앱 전제 항목 전부 숨김 (docs/실사_구환자앱_폐기_2026-09-14.md §3).
        // 폐기 계획 승인 후 제거. 신 환자관리(/patients)는 아래 슬림 메뉴 care 그룹에서 플래그·베타 게이트로 노출.
        // 항목이 비면 렌더 단계에서 그룹 자체가 표시되지 않음 (group.items.length 0 → null)
        patient: {
            name: '환자 관리', icon: Users,
            items: [
                // { name: '환자앱 홍보 페이지', icon: Megaphone, path: '/clinic/patient-app-promo' },
                // { name: '공지사항', icon: Megaphone, path: '/clinic/offers' },
                // { name: '운영 정보 관리 (충실도)', icon: Sparkles, path: '/clinic/profile-completeness' },
                // { name: '내 환자 (단골)', icon: Users, path: '/clinic/my-patients' },
                // { name: '의료기록 전송', icon: Send, path: '/clinic/send-record' },
            ]
        },
        core: {
            name: '기공소', icon: Search,
            items: [
                ...(phaseOpen(4) ? [
                    // 기공소 찾기 — 치과 메뉴에서 숨김 (대표 지시 2026-08-15). 라우트·기능 유지, 필요 시 주석 해제
                    // { name: '기공소 찾기', icon: Search, path: '/find-lab' },
                    { name: 'A/S 재제작 상담', icon: ClipboardList, path: '/as-remake' },
                    { name: '보철 갤러리', icon: Camera, path: '/feed' },
                ] : []),
            ]
        },
        locked: {
            name: '주요 기능 (유료)', icon: Lock,
            items: [
                { name: '대시보드', icon: LayoutDashboard, path: '/upgrade', locked: true },
                { name: '받은 의뢰서', icon: Package, path: '/upgrade', locked: true },
                // 채팅 숨김 (대표 지시 2026-08-11) — 기능·라우트는 그대로, 이 줄만 풀면 복원
                // { name: '채팅', icon: MessageSquare, path: '/upgrade', locked: true },
                { name: '거래명세서', icon: FileText, path: '/upgrade', locked: true },
            ]
        },
        settings: {
            name: '설정', icon: Settings,
            items: [
                { name: '설정', icon: Settings, path: '/settings' },
            ]
        },
    };

    // ── 기공소 무료 메뉴 ──
    const labFreeMenus = {
        core: {
            name: '홍보', icon: Megaphone,
            items: [
                ...(phaseOpen(4) ? [
                    { name: '홍보페이지', icon: Megaphone, path: '/lab-advertising', highlight: true },
                    // 테스트보철 게시판 — 기공소 메뉴에서 숨김 (대표 지시 2026-08-15). 라우트·기능은 유지, 필요 시 주석 해제
                    // { name: '테스트보철 게시판', icon: ClipboardList, path: '/test-cases' },
                    { name: '보철 갤러리', icon: Camera, path: '/feed' },
                ] : []),
            ]
        },
        locked: {
            name: '주요 기능 (유료)', icon: Lock,
            items: [
                { name: '대시보드', icon: LayoutDashboard, path: '/upgrade', locked: true },
                { name: '받은 의뢰서', icon: Package, path: '/upgrade', locked: true },
                // 채팅 숨김 (대표 지시 2026-08-11) — 기능·라우트는 그대로, 이 줄만 풀면 복원
                // { name: '채팅', icon: MessageSquare, path: '/upgrade', locked: true },
            ]
        },
        upgrade: {
            name: '업그레이드', icon: Zap,
            items: [
                { name: '모집 참여하기', icon: Zap, path: '/subscription', highlight: true },
            ]
        },
        // 2026-10-21: 설정류 그룹 키를 'settings'→'tools'(비PIN)로 — lab은 'settings' 키를 쓰지 않아 PIN 축소가 전역 배열 변경 없이 성립
        tools: {
            name: '도구·설정', icon: Settings,
            items: [
                // 🧩 지그 생성 — 플래그 ON 또는 베타(아이반) (2026-10-19)
                ...((jigLocalOn || jigWebOn) ? [{ name: '지그·3D 모델', icon: Factory, path: '/jig' }] : []),   // 외주 의뢰(PC)는 외주 의뢰서 「새 의뢰」 안으로(10-30)
                // '완성 디자인'(/design-results) 메뉴 제거 (2026-10-21 대표) — 라우트·플래그 designResultsOn 로직은 보존, 메뉴 노출만 삭제
                { name: '설정', icon: Settings, path: '/settings' },
            ]
        },
    };

    // ── 직원 메뉴 (치과) ──
    const staffClinicMenus = {
        ...receptionGroup,   // 🧾 접수 — 맨 위 (2026-10-03, 직원도 데스크 업무)
        core: {
            name: '기공 관리', icon: FileText,   // 2026-10-06 대표 지시: '의뢰' → '기공 관리'
            // 치과 직원용 — 기공소용 coreItems(외주 등) 대신 치과 업무 항목 (2026-07-19)
            items: [
                { name: '기공 캘린더', icon: LayoutDashboard, path: '/clinic-today' },   // 2026-10-07 대표 지시: '오늘' → '기공 캘린더'
                { name: '의뢰서 작성', icon: FilePlus, path: '/create-order' },
                { name: '보낸 의뢰서', icon: Package, path: '/orders' },
                ...(!deskReceptionOn ? patientsSoonItem : []),   // 2026-10-30: 환자 자동관리·재내원 안내·예약 → '환자 자동관리 (준비중)' 하나. 접수 ON이면 접수 그룹에
                // 제작 현황 — 치과에서는 「오늘」에 통합 (대표 확정 2026-08-11). 기공소 메뉴에는 그대로 있음
                // { name: '제작 현황', icon: Factory, path: '/production' },
                // 스캔 수집: 플래그 ON && (베타 || stage 2) — 판정은 scanIntakeOn 헬퍼 하나로 통일 (2026-08-20, 구 phaseOpen(3) 제거)
                ...(scanIntakeOn ? [{ name: '최근 스캔', icon: ScanLine, path: '/clinic/scans' }] : []),
            ],
        },
        // 치과 직원 서류 개방 (2026-08-05 대표 확정) — 비베타 치과 대표 메뉴와 동일 구성
        billing: {
            name: '받은 서류', icon: FileSpreadsheet,
            items: [
                { name: '받은 명세서', icon: FileSpreadsheet, path: '/transactions' },   // 대표 10-15 다시 표시
                // { name: '받은 계산서 (준비 중)', icon: FileText, path: '/tax-invoices' },   // 준비 중 표기 — 대표 지시 2026-10-04   // 숨김 — 대표 10-15 "나중에 작업할 때 다시" (라우트도 App.js 에서 막음)
                // { name: '정산 확인 (준비 중)', icon: ClipboardList, path: '/clinic/settlement' },   // 숨김 — 대표 10-15 "나중에 작업할 때 다시" (라우트도 App.js 에서 막음)
            ],
        },
        service: {
            name: '서비스', icon: Globe,
            items: [
                // { name: '마켓', icon: ShoppingBag, path: '/marketplace' }, // 추후 활성화
                // { name: '업체소개', icon: Search, path: '/lab-directory' }, // 일시 숨김
                // { name: '공감마당', icon: Handshake, path: '/empathy-board' },
                // { name: '커뮤니티', icon: Globe, path: '/community' },
            ]

        },
        settings: {
            name: '설정', icon: Settings,
            items: [
                { name: '설정', icon: Settings, path: '/settings' },
            ]
        },
    };

    // ── 직원 메뉴 (기공소) — 경영 그룹 제거 ──
    const staffLabMenus = {
        core: {
            name: '작업', icon: FileText,
            items: [...coreItems, { name: '외주 의뢰서', icon: Zap, path: '/outsource-request' }],   // 10-30: 옛 '외주' 대신 외주 의뢰서(직원도 보냄 — rules 허용)
        },
        service: {
            name: '서비스', icon: Globe,
            items: [
                // { name: '마켓', icon: ShoppingBag, path: '/marketplace' }, // 추후 활성화
                // 기공소에서는 업체소개 숨김
                // { name: '공감마당', icon: Handshake, path: '/empathy-board' },
                // { name: '커뮤니티', icon: Globe, path: '/community' },
            ]

        },
        settings: {
            name: '설정', icon: Settings,
            items: [
                { name: '설정', icon: Settings, path: '/settings' },
            ]
        },
    };

    // ── 외주업체 대표 메뉴 ──
    // 🏭 제조사(outsource) 목차 — CNC 파트너 화면이 기본 (대표 10-23 "제조사 회원가입으로 다시 구현, 목차도")
    //   홈·케이스·기공소·정산·설정 = 파트너 화면(현재 샘플 데이터) / 기존 외주 화면은 「기존 외주 화면」 묶음으로 보존(기능·라우트 그대로)
    const partnerOutsourceMenus = {
        partner: {
            name: 'CNC 파트너', icon: Factory,
            items: [
                { name: '홈', icon: Home, path: '/partner' },
                { name: '케이스', icon: Package, path: '/partner/cases' },
                { name: '기공소', icon: Building2, path: '/partner/labs' },
                { name: '정산', icon: Wallet, path: '/partner/settlement' },
                { name: '설정', icon: Settings, path: '/partner/settings' },
            ]
        },
        legacy: {
            name: '외주', icon: FileText,   // 10-30: 옛 외주 화면(물량 정리·거래명세서·통계) 삭제
            items: [
                // 외주 의뢰서 확장(10-13): CNC 제조업체 받은 의뢰·연결 요청 — 스위치(outsourceMf) ON 이거나 베타일 때만
                ...(isOutsourceMfOn(features, userData, auth.currentUser?.email) ? [{ name: '외주 의뢰서', icon: Zap, path: '/outsource-request' }] : []),
                { name: '외주업체홍보', icon: Megaphone, path: '/outsource-advertising' },
                // 채팅 숨김 (대표 지시 2026-08-11) — 기능·라우트는 그대로, 이 줄만 풀면 복원
                // { name: '채팅', icon: MessageSquare, path: '/chat' },
            ]
        },
        account: {
            name: '계정', icon: User,
            items: [
                { name: '직원목록', icon: Users, path: '/company-manager' },
                { name: '거래처목록', icon: Building2, path: '/connections' },
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };

    // ── 직원 메뉴 (제조사) — 작업 화면(홈·케이스)만, 정산·설정은 대표 ──
    const partnerStaffOutsourceMenus = {
        partner: {
            name: 'CNC 파트너', icon: Factory,
            items: [
                { name: '홈', icon: Home, path: '/partner' },
                { name: '케이스', icon: Package, path: '/partner/cases' },
            ]
        },
        legacy: {
            name: '외주', icon: FileText,
            items: [{ name: '외주 의뢰서', icon: Zap, path: '/outsource-request' }],   // 10-30: 옛 외주 화면 삭제 — 받은 의뢰는 외주 의뢰서에서
        },
        account: {
            name: '계정', icon: User,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };

    // 🏭 partnerHome 스위치 OFF(기본)면 기존 외주업체 목차 그대로 (시뮬레이션 10-23 [상]1 — 배포 사고 방지)
    const legacyOutsourceMenus = {
        work: {
            name: '주문관리', icon: FileText,
            items: [
                // 외주 의뢰서 확장(10-13): CNC 제조업체 받은 의뢰·연결 요청 — 스위치(outsourceMf) ON 이거나 베타일 때만
                ...(isOutsourceMfOn(features, userData, auth.currentUser?.email) ? [{ name: '외주 의뢰서', icon: Zap, path: '/outsource-request' }] : []),
                // 채팅 숨김 (대표 지시 2026-08-11) — 기능·라우트는 그대로, 이 줄만 풀면 복원
                // { name: '채팅', icon: MessageSquare, path: '/chat' },
            ]
        },
        service: {
            name: '서비스', icon: Globe,
            items: [
                // { name: '마켓', icon: ShoppingBag, path: '/marketplace' }, // 추후 활성화
            ]
        },
        management: {
            name: '경영관리', icon: Settings,
            items: [
                { name: '직원목록', icon: Users, path: '/company-manager' },
                { name: '외주 거래명세서', icon: FileText, path: '/vendor-statements' },   // 10-30: 업체 전용 새 화면(VendorStatements) — 기공소용 /statements 와 분리(대표 "기존 거래명세서랑 오류가 날까봐")
                { name: '거래처목록', icon: Building2, path: '/connections' },
                { name: '외주업체홍보', icon: Megaphone, path: '/outsource-advertising' },
            ]
        },
        account: {
            name: '계정', icon: User,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };
    const legacyStaffOutsourceMenus = {
        work: {
            name: '주문관리', icon: FileText,
            items: [{ name: '외주 의뢰서', icon: Zap, path: '/outsource-request' }],   // 10-30: 옛 '외주 물량 정리' 삭제 — 받은 의뢰는 외주 의뢰서에서
        },
        service: {
            name: '서비스', icon: Globe,
            items: []
        },
        account: {
            name: '계정', icon: User,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };
    const partnerOn = isPartnerHomeOn(features, userData);
    const outsourceMenus = partnerOn ? partnerOutsourceMenus : legacyOutsourceMenus;
    const staffOutsourceMenus = partnerOn ? partnerStaffOutsourceMenus : legacyStaffOutsourceMenus;

    // 판매자(seller) 메뉴 - 승인된 판매자 (v4 스펙)
    const sellerMenus = {
        dashboard: {
            name: '📊 대시보드',
            icon: LayoutDashboard,
            items: [
                { name: '대시보드', icon: LayoutDashboard, path: '/seller-dashboard' },
            ]
        },
        orders: {
            name: '📦 주문관리',
            icon: ShoppingCart,
            items: [
                { name: '주문관리', icon: ShoppingCart, path: '/order-management' },
                { name: '주문내역', icon: FileText, path: '/seller-orders' },
            ]
        },
        products: {
            name: '🏷 상품관리',
            icon: Package,
            items: [
                { name: '상품목록', icon: Package, path: '/product-management' },
                { name: '상품등록', icon: FilePlus, path: '/product-registration' },
                { name: '대량상품등록', icon: FileSpreadsheet, path: '/bulk-product-upload' },
            ]
        },
        settlement: {
            name: '💰 정산내역',
            icon: DollarSign,
            items: [
                { name: '정산내역', icon: DollarSign, path: '/seller-settlement' },
            ]
        },
        market: {
            name: '🛒 마켓',
            icon: ShoppingBag,
            items: [
                // { name: '마켓', icon: ShoppingBag, path: '/marketplace' }, // 추후 활성화
            ]
        },
        chat: {
            name: '💬 채팅',
            icon: MessageSquare,
            items: [
                // 채팅 숨김 (대표 지시 2026-08-11) — 기능·라우트는 그대로, 이 줄만 풀면 복원
                // { name: '채팅', icon: MessageSquare, path: '/chat' },
            ]
        },
        mypage: {
            name: '👤 마이페이지',
            icon: User,
            items: [
                { name: '스토어 설정', icon: Building2, path: '/seller-store-settings' },
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };

    // 판매자(seller) 메뉴 - 미승인 판매자
    const sellerPendingMenus = {
        shop: {
            name: '판매자 신청',
            icon: ShoppingBag,
            items: [
                { name: '승인요청', icon: FilePlus, path: '/seller-application' },
                { name: '승인현황', icon: FileText, path: '/seller-application-status' },
            ]
        },
        settings: {
            name: '설정',
            icon: Settings,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };

    // 재료회사(supplier) 메뉴 - 승인된 재료회사
    const supplierMenus = {
        supplier: {
            name: '재료회사',
            icon: Building2,
            items: [
                { name: '마켓플레이스', icon: ShoppingBag, path: '/marketplace' },
                { name: '상품등록', icon: Package, path: '/product-registration' },
                { name: '상품관리', icon: FileText, path: '/product-management' },
            ]
        },
        settings: {
            name: '설정',
            icon: Settings,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
                { name: '영업사원관리', icon: Users, path: '/sales-rep-management' },
            ]
        }
    };

    // 재료회사(supplier) 메뉴 - 미승인 재료회사
    const supplierPendingMenus = {
        supplier: {
            name: '재료회사',
            icon: Building2,
            items: [
                { name: '승인현황', icon: FileText, path: '/seller-application-status' },
            ]
        },
        settings: {
            name: '설정',
            icon: Settings,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
            ]
        }
    };

    // 관리자(admin) 메뉴 — 기능별 그룹화 (2026-05)
    const adminMenus = {
        analytics: {
            name: '분석·통계',
            icon: BarChart3,
            items: [
                { name: '플랫폼분석', icon: BarChart3, path: '/admin/analytics' },
            ]
        },
        members: {
            name: '회원·승인',
            icon: Users,
            items: [
                { name: '회원관리', icon: Users, path: '/admin/user-management' },
                // 고아 라우트 메뉴 연결 (2026-07-24 관리자 점검): 구독·거래문의는 핵심 화면인데 메뉴가 없었음
                { name: '구독 현황', icon: DollarSign, path: '/admin/subscriptions' },
                { name: '거래문의 관리', icon: MessageSquare, path: '/admin/match-requests' },
                { name: 'A/S 재제작 연결', icon: ClipboardList, path: '/admin/as-remake' },
                { name: '해외 기공소 심사', icon: Users, path: '/admin/global-labs' },
                { name: '외주 업체 재심사', icon: ShieldCheck, path: '/admin/outsource-vendors' },   // 2026-10-13 외주 의뢰서 확장 → S5(10-27) 재심사(종류 변경·보류 해제·GMP 만료만, 신규는 회원관리 승인)
                { name: '글로벌 수수료', icon: BarChart3, path: '/admin/global-commission' },
                { name: '기공소홍보', icon: Megaphone, path: '/admin/lab-advertising' },
            ]
        },
        patient: {
            name: '환자 플랫폼 (치과)',
            icon: Phone,
            items: [
                { name: 'HIRA 치과 동기화', icon: Cloud, path: '/admin/hira-sync' },
                { name: '가입 치과 클레임 관리', icon: ShieldCheck, path: '/admin/clinic-claim-manager', highlight: true },
                { name: '공공 데이터 동기화', icon: Cloud, path: '/admin/public-data-sync', highlight: true },
                { name: 'HIRA 비급여 (단일)', icon: Cloud, path: '/admin/hira-nonpayment-sync' },
                { name: '치과 클레임 검수', icon: ShieldCheck, path: '/admin/hospital-claim-review' },
                { name: 'Legacy 매칭 마이그레이션', icon: ShieldCheck, path: '/admin/claim-migration' },
                { name: '치료 추천 규칙', icon: Sparkles, path: '/admin/care-recommendations' },
            ]
        },
        labdata: {
            name: '기공소 데이터',
            icon: Cloud,
            items: [
                { name: 'LOCALDATA 기공소 동기화', icon: Cloud, path: '/admin/localdata-sync' },
            ]
        },
        content: {
            name: '콘텐츠 관리',
            icon: FileText,
            items: [
                { name: '중고물품관리', icon: Package, path: '/admin/used-item-management' },
                { name: '구인관리', icon: Briefcase, path: '/admin/job-management' },
                { name: '세미나관리', icon: GraduationCap, path: '/admin/seminar-management' },
                { name: '피드백관리', icon: MessageCircle, path: '/admin/feedbacks' },
            ]
        },
        broker: {
            name: '추천·중개',
            icon: Gift,
            items: [
                { name: 'A/S 신청 관리', icon: Phone, path: '/admin/as-leads' },
                { name: '신규 개원 치과', icon: Building2, path: '/admin/new-clinics' },   // 심평원 수집 (2026-09-03)
                { name: '전국 치과 목록', icon: Hospital, path: '/admin/clinics' },   // 심평원 hospitals 전체 (2026-09-06)
                { name: '신규 개설·폐업 기공소', icon: Factory, path: '/admin/new-labs' },   // 행안부 인허가 수집 (2026-09-03)
                { name: '추천인 관리', icon: Gift, path: '/admin/referrals' },
                { name: '테스트보철 현황', icon: ClipboardList, path: '/admin/test-cases' },
                { name: '매칭 수수료', icon: Percent, path: '/admin/commissions' },
                { name: '갤러리 스폰서', icon: Megaphone, path: '/admin/gallery-sponsors' },
            ]
        },
        settings: {
            name: '설정',
            icon: Settings,
            items: [
                { name: '프로필', icon: User, path: '/profile' },
                { name: '결제설정', icon: DollarSign, path: '/admin/payment-settings' },
                { name: '페이지설정', icon: Settings, path: '/admin/page-toggle' },
            ]
        }
    };

    // 메뉴 선택 로직
    // 🌏 해외 기공소 (labKind: 'overseas') — 영문 메뉴, 승인 전엔 상태 화면만
    const overseasPendingMenus = {
        global: {
            name: 'DentCluster Global', icon: FileText,
            items: [{ name: 'Account status', icon: Settings, path: '/global/status' }],
        },
    };
    const overseasMenus = {
        global: {
            name: 'DentCluster Global', icon: FileText,
            items: [
                { name: 'Home', icon: LayoutDashboard, path: '/global/status' },
                { name: 'Find Labs', icon: Users, path: '/global/labs' },
                { name: 'My Cases', icon: FileText, path: '/global/orders' },
                { name: 'Invoices', icon: FileText, path: '/global/invoices' },
                { name: 'Agent', icon: HardDriveDownload, path: '/global/agent' },
                // 채팅 숨김 (대표 지시 2026-08-11) — 해외 계정 메뉴도 동일
                // { name: 'Chats', icon: MessageSquare, path: '/chat' },
                { name: 'Settings', icon: Settings, path: '/settings' },
            ],
        },
    };

    const getMenus = () => {
        // 관리자
        if (isAdmin) {
            return adminMenus;
        }
        // 해외 기공소
        if (userData?.labKind === 'overseas') {
            return userData?.globalStatus === 'active' ? overseasMenus : overseasPendingMenus;
        }
        // 판매자
        if (isSeller) {
            // 승인된 판매자
            if (userData?.sellerStatus === 'approved') {
                return sellerMenus;
            }
            // 미승인 판매자
            return sellerPendingMenus;
        }
        // 재료회사
        if (isSupplier) {
            // 승인된 재료회사
            if (userData?.sellerStatus === 'approved') {
                return supplierMenus;
            }
            // 미승인 재료회사
            return supplierPendingMenus;
        }
        // 개인회원
        if (isIndividual) {
            return individualMenus;
        }
        // 직원
        if (isStaff) {
            const staffBusinessType = userData?.companyBusinessType;
            if (staffBusinessType === 'clinic' || staffBusinessType === 'dental' || staffBusinessType === '치과') {
                return staffClinicMenus;
            }
            if (staffBusinessType === 'outsource') return staffOutsourceMenus;
            // 결제 전 기공소(제한 모드) 직원 — W4
            if (userData?.accessMode === 'lab_limited') return labLimitedStaffMenus;
            // 1차: 직원 계정은 명세서·계산서 라우트가 차단돼 있으므로 메뉴에서도 제외 (모순 방지, 2026-07-24 사장 결정 — 직원 기능은 3~4차)
            if (isBeta) return staffLabMenus;
            return {
                core: {
                    name: '작업', icon: FileText,
                    items: [
                        ...(features?.clinicInvite === true ? [{ name: '받은 의뢰서', icon: Package, path: '/orders' }] : []),
                        // 제작 현황 보드 — 기공소 직원(기술자)도 접근 (2026-08-05 대표 확정)
                        { name: '기공 현황', icon: Factory, path: '/production' },
                    ],
                },
                settings: {
                    name: '설정', icon: Settings,
                    items: [{ name: '설정', icon: Settings, path: '/settings' }],
                },
            };
        }
        // 업체 대표
        if (businessType === 'clinic' || businessType === 'dental' || businessType === '치과') {
            // 2026-07-24 사이드메뉴 개편 B안: 비베타(신규·초대) 치과는 간소화 메뉴 — 받는 문서 중심
            // 2026-08-07 대표 확정: 대시보드 개방(마감 칩+제작현황 통합 보드) — 맨 위 추가, 나머지 슬림 유지
            if (!isBeta) {
                return {
                    ...receptionGroup,   // 🧾 접수 — 맨 위 (2026-10-03 대표 지시)
                    care: {
                        name: '기공 관리', icon: FileText,   // 2026-10-06 대표 지시: '의뢰' → '기공 관리'
                        items: [
                            { name: '기공 캘린더', icon: LayoutDashboard, path: '/clinic-today' },   // 2026-10-07 대표 지시: '오늘' → '기공 캘린더'
                            { name: '의뢰서 작성', icon: FilePlus, path: '/create-order' },
                            { name: '보낸 의뢰서', icon: Package, path: '/orders' },
                            ...(!deskReceptionOn ? patientsSoonItem : []),   // 2026-10-30: 환자 자동관리·재내원 안내·예약 → '환자 자동관리 (준비중)' 하나. 접수 ON이면 접수 그룹에
                            // 제작 현황 — 「오늘」에 통합 (대표 확정 2026-08-11)
                            // { name: '제작 현황', icon: Factory, path: '/production' },
                            // 2026-10-06 대표 지시: 원장 메뉴·기공소 그룹 항목을 기공 관리 맨 아래로
                            { name: '기공비 분석', icon: Wallet, path: '/lab-cost' },
                            { name: '재제작 분석', icon: RotateCcw, path: '/remake-analysis' },
                            ...((isClinicSubscriptionOn(features) || isAllFree(features)) ? [{ name: 'A/S 재제작 상담', icon: ClipboardList, path: '/as-remake' }] : []),
                        ],
                    },
                    billing: {
                        name: '받은 서류', icon: FileSpreadsheet,
                        items: [
                            { name: '받은 명세서', icon: FileSpreadsheet, path: '/transactions' },   // 대표 10-15 다시 표시
                            // { name: '받은 계산서 (준비 중)', icon: FileText, path: '/tax-invoices' },   // 준비 중 표기 — 대표 지시 2026-10-04   // 숨김 — 대표 10-15 "나중에 작업할 때 다시" (라우트도 App.js 에서 막음)
                            // { name: '정산 확인 (준비 중)', icon: ClipboardList, path: '/clinic/settlement' },   // 숨김 — 대표 10-15 "나중에 작업할 때 다시" (라우트도 App.js 에서 막음)
                        ],
                    },
                    // 2026-10-06 대표 지시: '원장 메뉴'·'기공소' 그룹 제거 — 항목은 위 care 맨 아래(기공비·재제작 분석 잠금은 SensitiveGate, A/S는 구독 스위치 조건 유지)
                    automation: {
                        name: '연결 프로그램', icon: HardDriveDownload,
                        items: [
                            // 치과 사이드메뉴에서 덴클 프로그램 제거 (대표 지시 2026-10-04) — /download-agent 라우트는 유지. 복원 시 아래 주석 해제
                // ...((scanIntakeOn || isClinicSubscriptionOn(features) || isAllFree(features)) ? [{ name: '덴클 프로그램', icon: HardDriveDownload, path: '/download-agent' }] : []),
                        ],
                    },
                    settings: {
                        name: '설정', icon: Settings,
                        items: [{ name: '설정', icon: Settings, path: '/settings' }],
                    },
                };
            }
            return clinicMenus; // 베타(아이반 연결) 치과는 기존 전체 메뉴 유지
        }
        if (businessType === 'outsource') return outsourceMenus;
        // 결제 전 기공소(제한 모드) — W4. allFree=true 면 App.js 가 full 로 판정하므로 이 분기를 타지 않는다
        if (userData?.accessMode === 'lab_limited') return labLimitedMenus;
        // 1차(거래명세서 서비스): 비베타 기공소는 슬림 메뉴 — 명세서/계산서/에이전트/설정만
        if (!isBeta) return labPhase1Menus;
        return isSubscribed ? labMenus : labFreeMenus;
    };

    const menus = getMenus();
    const categoryKeys = Object.keys(menus);
    const currentMenu = menus[activeCategory] || menus[categoryKeys[0]];

    const isActive = (path) => location.pathname === path;

    // 계정 영역(하단) 역할 표기 — 시각 전용, 기존 businessType·직원/대표 구분 재사용
    const acctRole = (() => {
        const bt = businessType;
        const base = (bt === 'clinic' || bt === 'dental' || bt === '치과') ? '치과'
            : (bt === 'lab' || bt === '기공소') ? '기공소'
            : (bt === 'outsource') ? '제조사'
            : isSeller ? '판매자'
            : isAdmin ? '관리자' : '';
        const who = isStaff ? '직원' : (isBusinessOwner ? '대표' : '');
        return [base, who].filter(Boolean).join(' · ');
    })();

    // 새 200px 텍스트 사이드바 적용 대상 (치과/기공소/외주/개인회원/관리자)
    const isClinicOrLabUser = (() => {
        // 관리자도 텍스트 사이드바 사용 (아이콘 전용 사이드바 대체, 2026-07-14)
        if (isAdminUser(userData)) return true;
        if (isIndividual) return true;
        // 판매자(승인/미승인 모두) 200px 텍스트 사이드바 사용
        if (isSeller) return true;
        const bt = businessType;
        if (bt === 'clinic' || bt === 'dental' || bt === '치과' || bt === 'lab' || bt === '기공소' || bt === 'outsource') return true;
        if (isStaff) {
            const sbt = userData?.companyBusinessType;
            if (sbt === 'clinic' || sbt === 'dental' || sbt === '치과' || sbt === 'lab' || sbt === '기공소' || sbt === 'outsource') return true;
        }
        return false;
    })();

    return (
        <div style={styles.container}>
            {/* ── 치과/기공소: 200px 텍스트 사이드바 ── (임베드 모드에서는 사이드바 전체 숨김 — 프로그램 사이드바와 이중 메뉴 방지) */}
            {!isEmbedded && (isClinicOrLabUser ? (
                <div className="dc-pside">
                    <style dangerouslySetInnerHTML={{ __html: PREMIUM_SIDEBAR_CSS }} />
                    <div className="dc-brand">
                        <div className="dc-mark">덴</div>
                        <div className="dc-brand-wrap">
                            <div className="dc-brand-nm">{userData?.businessName || userData?.name || '덴탈클러스터'}</div>
                            <div className="dc-brand-sub">{isStaff ? '직원 계정' : '덴탈클러스터'}</div>
                        </div>
                        <NotificationBell user={userData} />
                    </div>
                    <div className="dc-body">
                        {categoryKeys.map(groupKey => {
                            const group = menus[groupKey];
                            if (!group?.items?.length) return null;   // 항목이 모두 숨겨진 섹션은 표시하지 않음
                            const isGroupLocked = pinProtectedGroups.includes(groupKey) && needsPinForManagement();
                            // 2026-07-30: owner 구역 잠금 시 항목명까지 숨기고 큰 열기 버튼으로 (회의 확정)
                            if (groupKey === 'owner' && isGroupLocked) {
                                return (
                                    <div key={groupKey} className="dc-sec">
                                        <button className="dc-lockcard" onClick={() => { setPendingNavigationPath(null); openPinModal(); }}>
                                            <div className="dc-lockcard-t"><Lock size={14} /> 정산·설정 — 눌러서 열기</div>
                                            <div className="dc-lockcard-d">거래명세서 · 예상 매출 · 거래처 · 설정</div>
                                        </button>
                                    </div>
                                );
                            }
                            const isOwnerOpen = groupKey === 'owner' && !isGroupLocked && isBusinessOwner && !isSeller && !isAdmin;
                            const isPinSec = pinProtectedGroups.includes(groupKey);   // PIN 보호 섹션 eyebrow 자물쇠 배지
                            return (
                                <div key={groupKey} className="dc-sec">
                                    <div className="dc-eyebrow">
                                        <span>{group.name}</span>
                                        {isPinSec && (
                                            <span className="dc-lk"><Lock size={9} /> PIN</span>
                                        )}
                                        {isOwnerOpen && (
                                            <span className="dc-eyebrow-right">
                                                <span style={{ fontSize: 10, color: 'var(--dc-accent)', fontWeight: 800 }}>열림</span>
                                                <button onClick={lockOwnerZone} title="대표 메뉴 잠그기"
                                                    style={{ fontSize: 10, fontWeight: 800, color: 'var(--dc-lock)', background: 'var(--dc-lock-soft)', border: 'none', borderRadius: 6, padding: '1px 7px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                    잠그기
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    {group.items.map(item => {
                                        // 정돈형 플랫 리스트 (2026-10-21 고급 시안) — 아이콘 faint, 활성 시 좌측 액센트 바+틴트+액센트 아이콘. 색은 토큰
                                        const active = isActive(item.path);
                                        const Icon = item.icon;
                                        const muted = item.locked || item.soon;
                                        return (
                                            <button key={item.path + item.name} onClick={() => handleSidebarItemClick(groupKey, item)}
                                                className={`dc-item${active ? ' on' : ''}${muted ? ' muted' : ''}`}>
                                                <span className="dc-ic">
                                                    {Icon ? <Icon size={16} strokeWidth={1.8} /> : <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />}
                                                </span>
                                                <span className="dc-tx">{item.name}</span>
                                                {item.soon && !item.name.includes('준비중') && (   /* 이름에 이미 '(준비중)'이 있으면 배지 생략 (2026-10-30) */
                                                    <span style={{ marginLeft: 'auto', background: 'var(--dc-active-bg)', color: 'var(--dc-muted)', fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '2px 7px' }}>준비중</span>
                                                )}
                                                {item.path === '/chat' && chatUnreadCount > 0 && (
                                                    <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>{chatUnreadCount}</span>
                                                )}
                                                {/* 🗺️ 확인할 답 건수 — 중립색(경보색 금지, 법무 RM §2-3) */}
                                                {item.path === '/patients' && roadmapInboxCount > 0 && (
                                                    <span style={{ marginLeft: 'auto', background: 'var(--dc-accent-soft)', color: 'var(--dc-accent)', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>{roadmapInboxCount}</span>
                                                )}
                                                {/* 📅 새 예약 요청 건수 — 중립색 */}
                                                {item.path === '/patients/bookings' && bookingNewCount > 0 && (
                                                    <span style={{ marginLeft: 'auto', background: 'var(--dc-accent-soft)', color: 'var(--dc-accent)', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>{bookingNewCount}</span>
                                                )}
                                                {item.path === '/download-agent' && agentUpdateNeeded && (
                                                    <span style={{ marginLeft: 'auto', background: 'var(--dc-lock-soft)', color: 'var(--dc-lock)', fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '2px 7px', whiteSpace: 'nowrap' }}>업데이트</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                    <div className="dc-foot">
                        <div className="dc-av">{(userData?.businessName || userData?.name || '덴').trim().charAt(0) || '덴'}</div>
                        <div style={{ minWidth: 0 }}>
                            <div className="dc-foot-who">{userData?.businessName || userData?.name || ''}</div>
                            <div className="dc-foot-role">{acctRole || '덴탈클러스터'}</div>
                        </div>
                        <div className="dc-foot-actions">
                            <button className="dc-foot-btn" onClick={() => setShowInfoModal(true)} title="정보">ⓘ</button>
                            <button className="dc-foot-btn" onClick={handleLogout} title="로그아웃">로그아웃</button>
                        </div>
                    </div>
                </div>
            ) : (
                /* ── 관리자/판매자/재료회사/개인: 기존 80px 아이콘 사이드바 ── */
                <div style={styles.sidebar}>
                    <div style={styles.logo}><NotificationBell user={userData} /></div>
                    <div style={styles.categoryList}>
                        {categoryKeys.map(key => {
                            const category = menus[key];
                            const Icon = category.icon;
                            const isActiveCategory = activeCategory === key;
                            return (
                                <button key={key} onClick={() => handleCategoryClick(key, category)}
                                    style={{ ...styles.categoryBtn, backgroundColor: isActiveCategory ? '#eef2ff' : 'transparent', color: isActiveCategory ? '#6366f1' : '#64748b', position: 'relative' }}>
                                    <Icon size={22} />
                                </button>
                            );
                        })}
                    </div>
                    <div style={styles.sidebarFooter}>
                        <button onClick={() => setShowInfoModal(true)} style={styles.logoutBtn}><Info size={20} /></button>
                        <button onClick={handleLogout} style={styles.logoutBtn}><LogOut size={20} /></button>
                    </div>
                </div>
            ))}

            <div style={{ ...styles.main, marginLeft: isEmbedded ? 0 : (isClinicOrLabUser ? 236 : 80) }}>
                {/* 빠른 진입 헤더 (기공소 찾기/치과 찾기) — 사이드바에서 진입 가능하므로 제거 */}
                <div style={styles.content}>
                    {/* 상단 탭바: 치과/기공소·임베드에서는 숨김, 나머지는 기존대로 표시 */}
                    {!isEmbedded && !isClinicOrLabUser && (
                        <div style={styles.contentHeader}>
                            <div style={styles.tabList}>
                                {currentMenu?.items.map(item => {
                                    const Icon = item.icon;
                                    const active = isActive(item.path);
                                    const notImplemented = ['/lab-warranty'];
                                    const isNotImplemented = notImplemented.includes(item.path);
                                    return (
                                        <button key={item.path} onClick={() => {
                                            if (isNotImplemented) {
                                                alert('이 기능은 준비 중입니다.');
                                                return;
                                            }
                                            if (item.soon) { alert(item.soonText || '이 기능은 준비 중입니다.'); return; }   // 준비중 항목은 이동 없음 (2026-10-30)
                                            navigate(item.path);
                                        }}
                                            style={{ ...styles.tabBtn, backgroundColor: active ? '#6366f1' : '#f1f5f9', color: active ? '#fff' : '#64748b', opacity: isNotImplemented ? 0.6 : 1 }}>
                                            {Icon && <Icon size={16} />}<span>{item.name}</span>
                                            {item.path === '/download-agent' && agentUpdateNeeded && (
                                                <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 10.5, fontWeight: 700, borderRadius: 8, padding: '2px 7px', whiteSpace: 'nowrap' }}>업데이트</span>
                                            )}
                                            {item.path === '/chat' && chatUnreadCount > 0 && (
                                                <span style={{ background: active ? '#fff' : '#ef4444', color: active ? '#6366f1' : '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>{chatUnreadCount}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            <div style={styles.userInfo}>
                                {/* 빠른 진입: 기공소 찾기 / 치과 찾기 */}
                                <button
                                    onClick={() => navigate('/find-lab')}
                                    style={styles.quickFindBtn}
                                    title="전국 기공소 검색"
                                >
                                    <Search size={14} /> 기공소 찾기
                                </button>
                                <button
                                    onClick={() => navigate('/find-clinic')}
                                    style={{ ...styles.quickFindBtn, background: '#eef2ff', color: '#3730a3', borderColor: '#c7d2fe' }}
                                    title="전국 치과 검색"
                                >
                                    <Building2 size={14} /> 치과 찾기
                                </button>
                                <span style={styles.userName}>{userData?.businessName || userData?.name}</span>
                                {isIndividual && <span style={styles.userType}>개인</span>}
                                {isStaff && <span style={styles.userType}>직원</span>}
                                {isSeller && userData?.sellerStatus === 'approved' && (
                                    <span style={{ ...styles.userType, backgroundColor: '#d1fae5', color: '#065f46' }}>판매자</span>
                                )}
                                {isSeller && userData?.sellerStatus !== 'approved' && (
                                    <span style={{ ...styles.userType, backgroundColor: '#fef3c7', color: '#92400e' }}>승인대기</span>
                                )}
                                {isAdmin && <span style={{ ...styles.userType, backgroundColor: '#fee2e2', color: '#dc2626' }}>관리자</span>}
                            </div>
                        </div>
                    )}
                    {/* 🔔 원장 호출 데스크 띠 (2026-10-08) — 치과(대표·직원) && doctorCall 플래그 strict && 승인 대기·거절 아님 && 접수 화면(/reception) 밖에서만.
                        접수 화면 안에서는 3번째 카드 테두리 깜빡임이 대신한다. 평소 6px 회색 줄(연결 살아 있음), 미응답 deskShowSec 뒤 펼침 */}
                    {isClinicAcctForBadge && isDoctorCallOn(features) && !['pending', 'rejected'].includes(userData?.approvalStatus) && !isEmbedded && !location.pathname.startsWith('/reception') && (
                        <DoctorCallDeskStrip clinicId={userData?.companyId || userData?.uid} />
                    )}
                    {/* 📢 약관 v6.0 개정 고지 배너 — 게시 시작일 2026-08-17 (30일 고지 기산 근거, 시행 2026-09-17). 얇은 안내 줄, 닫기 시 사용자별 localStorage 기억 */}
                    {showPolicyBanner && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 14px', margin: '0 0 12px', fontSize: 13, color: '#3B4468', lineHeight: 1.6 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                약관이 개정되었습니다 (v6.0 · 시행 2026-09-17).
                                주요 변경: <strong>중개 수수료 전면 폐지</strong>(즉시 시행) · <strong>치과 회원 구독 신설</strong>(별도 공지 시행일부터 적용).{' '}
                                <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                                    전문 보기 →
                                </button>
                            </span>
                            <button onClick={dismissPolicyBanner} title="닫기"
                                style={{ background: 'none', border: 'none', padding: 2, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>
                                <X size={15} />
                            </button>
                        </div>
                    )}
                    {/* 📢 약관 v6.2 · 처리방침 v3.2.3 공고 안내 띠 — 게시 2026-10-04 ~ 2026-11-03 (문안 그대로, 계정 유형별) */}
                    {/* 약관 v6.2 안내 띠 내림 — 대표 지시 2026-10-04 "이용약관 공고없애줘". 약관 페이지(/terms) 공고일자·시행일자 표기는 유지. 복원: 아래 false 제거 */}
                    {false && policyV62Audience && !policyV62Dismissed && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 14px', margin: '0 0 12px', fontSize: 13, color: '#3B4468', lineHeight: 1.6 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                {policyV62Audience === 'lab' && (<>
                                    {'이용약관·개인정보처리방침이 바뀌었습니다(10월 4일 공고). 지금 쓰시는 기공소는 '}<strong>{'12월 31일까지 그대로 무료'}</strong>{'이고, 자동으로 결제되지 않습니다.'}<br />
                                    {'11월 3일부터는 이용이 멈춘 계정의 PC 덴클 프로그램 수집·파일 주고받기도 함께 멈춥니다. '}
                                </>)}
                                {policyV62Audience === 'clinic' && (<>
                                    {'이용약관·개인정보처리방침이 바뀌었습니다(10월 4일 공고). 지금 쓰시는 치과는 '}<strong>{'요금이 바뀌지 않고 계속 무료'}</strong>{'입니다(바뀌게 되면 30일 전에 따로 알려드립니다). '}
                                </>)}
                                {policyV62Audience === 'common' && (
                                    '이용약관·개인정보처리방침이 바뀌었습니다(10월 4일 공고, 처리방침은 10월 11일 시행). 지금 쓰시는 회원의 요금은 바뀌지 않습니다. '
                                )}
                                <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {'전문 보기 →'}
                                </button>
                            </span>
                            <button onClick={dismissPolicyV62} title="닫기"
                                style={{ background: 'none', border: 'none', padding: 2, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>
                                <X size={15} />
                            </button>
                        </div>
                    )}
                    {/* 📜 약관 v7.0 제4조의7 재확인 모달(14항) — 시행 후 required 일 때만 (위 delegationV70* 참고) */}
                    {delegationV70Open && delegationV70.required && (
                        <DelegationV70ReconfirmModal user={userData} isStaff={delegationV70.isStaff}
                            onClose={() => setDelegationV70Open(false)} onConfirmed={onDelegationV70Confirmed} />
                    )}
                    {/* 🛡️ 처리위탁 부속 확인 2건(데스크 접수·환자 앱 등록) 1회 모달 (2026-10-08 대표 "권고대로") — 치과 계정만.
                        제4조의7 재확인 모달이 떠 있거나 판정 중이면 기다린다(제4조의7 먼저, 부속 2개 뒤 — 법무검토 §2 ④) */}
                    {delegationV70Subject(userData).isClinic && (
                        <ClinicDelegationPrompt uid={userData?.uid} isStaff={delegationV70Subject(userData).isStaff}
                            enabled suspended={delegationV70Open || delegationV70.loading} />
                    )}
                    {/* 📢 약관 v8.0 전면 정비 공고 띠 — 공고 전(features 값 없음)엔 숨김. 공고~시행 사이만 (문안 termsV80.js, 정본 §4-4). 맨 위 */}
                    {policyV80Show && !policyV80Dismissed && v80BannerText(features) && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 14px', margin: '0 0 12px', fontSize: 13, color: '#3B4468', lineHeight: 1.6 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                {v80BannerText(features)}
                                <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {V80_BANNER_LINK}
                                </button>
                            </span>
                            <button onClick={dismissPolicyV80} title="닫기"
                                style={{ background: 'none', border: 'none', padding: 2, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>
                                <X size={15} />
                            </button>
                        </div>
                    )}
                    {/* 📢 약관 v7.1 개정 공고 띠 — 게시 2026-10-24 ~ 시행(2026-11-24) 전까지, v8.0 공고 시작 시 숨김 (문안 termsV71.js, 정본 §4). v7.0 띠 위에 둔다 */}
                    {policyV71Show && !policyV71Dismissed && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 14px', margin: '0 0 12px', fontSize: 13, color: '#3B4468', lineHeight: 1.6 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                {V71_BANNER_TEXT}
                                <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {V71_BANNER_LINK}
                                </button>
                            </span>
                            <button onClick={dismissPolicyV71} title="닫기"
                                style={{ background: 'none', border: 'none', padding: 2, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>
                                <X size={15} />
                            </button>
                        </div>
                    )}
                    {/* 📢 약관 v7.0 개정 공고 띠 — 게시 2026-10-06 ~ 시행(2026-11-06) 전까지 (문안 termsV70.js, 정본 §3-4 (가)) */}
                    {policyV70Show && !policyV70Dismissed && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 14px', margin: '0 0 12px', fontSize: 13, color: '#3B4468', lineHeight: 1.6 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                {V70_BANNER_TEXT}
                                <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {V70_BANNER_LINK}
                                </button>
                            </span>
                            <button onClick={dismissPolicyV70} title="닫기"
                                style={{ background: 'none', border: 'none', padding: 2, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>
                                <X size={15} />
                            </button>
                        </div>
                    )}
                    {/* 📢 처리방침 v3.2.8 개정 공고 띠 — 게시 2026-10-16 ~ 2026-10-30 (문안 §3-1 글자 그대로, v7.0 띠와 같은 모양) */}
                    {policyV328Show && !policyV328Dismissed && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 14px', margin: '0 0 12px', fontSize: 13, color: '#3B4468', lineHeight: 1.6 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                {V328_BANNER_TEXT}
                                <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {V328_BANNER_LINK}
                                </button>
                            </span>
                            <button onClick={dismissPolicyV328} title="닫기"
                                style={{ background: 'none', border: 'none', padding: 2, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>
                                <X size={15} />
                            </button>
                        </div>
                    )}
                    {/* 📢 처리방침 v3.3.5 개정 공고 띠 — 게시 2026-10-27 ~ 2026-11-10 (S5, v3.2.8 띠와 같은 모양. 문안 config/privacyV335.js ☆) */}
                    {policyV335Show && !policyV335Dismissed && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F5F7FF', border: '1px solid #DDE3F5', borderRadius: 8, padding: '8px 14px', margin: '0 0 12px', fontSize: 13, color: '#3B4468', lineHeight: 1.6 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                {V335_BANNER_TEXT}
                                <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', padding: 0, color: '#4F52C7', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {V335_BANNER_LINK}
                                </button>
                            </span>
                            <button onClick={dismissPolicyV335} title="닫기"
                                style={{ background: 'none', border: 'none', padding: 2, color: '#8A90A3', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>
                                <X size={15} />
                            </button>
                        </div>
                    )}
                    {/* 치과 매칭 검증 대기 배너 (pending_verification 상태일 때만 노출) */}
                    {userData?.businessType === 'dental' && <HospitalVerificationBanner />}
                    {/* 💰 예상 기공비·예상 명세서 기공소 대표 통합 고지 배너 (2차, 2026-10-07) — ☆ 문안 docs/법무검토_치과_기간예상명세서_2026-10-07.md §1-3 글자 그대로.
                        대표 결정(10-07): [재확인] 버튼 없음 — 기존 ack 키(expectedCostNoticeAckAt)·노출 조건 유지, 문안만 교체. {오픈일} = config/features.clinicExpectedStatementOpenAt(없으면 "곧") */}
                    {expectedCostNoticeShow && (
                        <div className="no-print" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', margin: '0 0 12px', fontSize: 13, color: '#78350F', lineHeight: 1.65 }}>
                            <div>
                                {'거래처 치과가 덴클에서 이 기공소의 예상 기공비를 봅니다. ① 「기공비 분석」의 이번 달 예상 기공비(의뢰 건수·치아 수·예상 금액·명세서 확정 금액)는 2026년 8월 13일부터 표시되어 왔고, ② '}
                                <b>{expStmtOpenAt ? `${expStmtOpenAt}부터는` : '곧'}</b>
                                {' 「받은 명세서」에서 치과가 기간을 골라 '}
                                <b>{'그 기간 의뢰 건을 환자 이름·치식·보철 종류·개수·개당 단가·금액까지 명세서 모양으로'}</b>
                                {' 볼 수 있습니다(제목·인쇄본에 "예상 — 거래명세서 아님" 표기, 기공소 검토 없이 단가표로 자동 계산). 지난 안내의 "품목별 단가는 보이지 않습니다"는 '}
                                {expStmtOpenAt ? `${expStmtOpenAt}부터 맞지 않습니다.` : '곧 맞지 않게 됩니다.'}
                                {' 치과에는 그 치과 자기 단가만 보이고 다른 치과 단가·기공소 전체 매출은 보이지 않습니다. 치과별로 끄려면 설정 › 치과에 공정 공개 › 예상 기공비·예상 명세서 표시(끄면 월별 금액·기간 품목 모두 꺼집니다). 기공소도 「예상 매출 조회」 [품목 보기]에서 치과가 보는 것과 같은 화면을 볼 수 있습니다.'}
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <button onClick={() => navigate('/settings/clinic-progress')}
                                    style={{ background: '#fff', border: '1px solid #F59E0B', color: '#92400E', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                                    설정에서 끄기
                                </button>
                                <button onClick={ackExpectedCostNotice} disabled={expectedCostNotice.busy}
                                    style={{ background: '#D97706', border: '1px solid #D97706', color: '#fff', borderRadius: 7, padding: '6px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', opacity: expectedCostNotice.busy ? 0.6 : 1 }}>
                                    {expectedCostNotice.busy ? '기록 중…' : '확인'}
                                </button>
                                {expectedCostNotice.error && <span style={{ fontSize: 12, color: '#B91C1C' }}>{expectedCostNotice.error}</span>}
                            </div>
                        </div>
                    )}
                    {/* 치과 가입 후 클레임 비활성 상태 안내 배너 (관리자 검토 대기) — 환자기능 미활용으로 임시 숨김. 되살리려면 주석 해제 */}
                    {/* {(userData?.businessType === 'dental' || userData?.businessType === 'clinic' || userData?.businessType === '치과') && <ClinicClaimPendingBanner />} */}
                    {/* 2026-07-30 대표 구역 라우트 가드 — 사이드메뉴를 안 거치고 주소를 직접 쳐도 잠금 (회의 발견 2) */}
                    {ownerZoneBlocked ? (
                        <div className="no-print" style={{ maxWidth: 460, margin: '80px auto', textAlign: 'center', background: '#FFFBEB', border: '1px dashed #f59e0b', borderRadius: 14, padding: '36px 24px' }}>
                            <Lock size={28} color="#92400E" style={{ marginBottom: 10 }} />
                            <div style={{ fontWeight: 800, fontSize: 16, color: '#92400E', marginBottom: 6 }}>대표 메뉴 화면입니다</div>
                            {/* 임베드에서는 사이드메뉴 [잠그기]가 없음 — 창 닫힘 시 에이전트가 잠금 처리 (0.9.1) */}
                            <div style={{ fontSize: 13, color: '#B45309', marginBottom: 18 }}>
                                {isEmbedded ? '핀을 입력하면 열립니다. 창을 닫으면 자동으로 잠깁니다.' : '핀을 입력하면 열립니다. 보고 나면 사이드메뉴의 [잠그기]를 눌러주세요.'}
                            </div>
                            <button onClick={() => { setPendingNavigationPath(null); openPinModal(); }}
                                style={{ padding: '10px 22px', border: 'none', borderRadius: 9, background: '#F59E0B', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                                핀 입력
                            </button>
                        </div>
                    ) : children}
                </div>
            </div>

            {/* PIN 입력 모달 */}
            {showPinModal && (
                <div style={styles.pinModalOverlay} onClick={closePinModal}>
                    <div style={styles.pinModal} onClick={e => e.stopPropagation()}>
                        <button style={styles.pinModalClose} onClick={closePinModal}><X size={20} /></button>
                        <div style={styles.pinIconBox}><Lock size={32} color="#6366f1" /></div>
                        {pinResetMode === 'code' ? (
                            <>
                                <h2 style={styles.pinModalTitle}>핀 재설정</h2>
                                <p style={styles.pinModalText}>가입 이메일로 보낸 6자리 인증코드와<br />새 핀을 입력하세요 (15분 유효)</p>
                                <input type="text" inputMode="numeric" maxLength={6} value={pinResetCode}
                                    onChange={(e) => setPinResetCode(e.target.value.replace(/\D/g, ''))}
                                    placeholder="이메일 인증코드 6자리" style={styles.pinInput} autoFocus />
                                <input type="password" inputMode="numeric" maxLength={6} value={pinResetNew}
                                    onChange={(e) => setPinResetNew(e.target.value.replace(/\D/g, ''))}
                                    placeholder="새 핀 (4~6자리)" style={{ ...styles.pinInput, marginTop: 8 }} />
                                {pinError && <p style={styles.pinError}>{pinError}</p>}
                                <button onClick={submitPinReset} disabled={pinVerifying || pinResetCode.length !== 6 || pinResetNew.length < 4}
                                    style={{ ...styles.pinBtn, opacity: (pinVerifying || pinResetCode.length !== 6 || pinResetNew.length < 4) ? 0.5 : 1 }}>
                                    {pinVerifying ? '확인 중…' : '새 핀으로 설정'}
                                </button>
                                <button onClick={() => { setPinResetMode(''); setPinError(''); }}
                                    style={{ marginTop: 8, border: 'none', background: 'transparent', color: '#94a3b8', fontSize: 12.5, cursor: 'pointer' }}>
                                    ← 핀 입력으로 돌아가기
                                </button>
                            </>
                        ) : (
                            <>
                                <h2 style={styles.pinModalTitle}>대표 메뉴 열기</h2>
                                {/* 첫 도입 1회 안내 (2026-07-30) */}
                                {localStorage.getItem('dcOwnerNoticeSeen') !== '1' && (
                                    <p style={{ fontSize: 12.5, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', margin: '0 0 10px', lineHeight: 1.5 }}>
                                        거래명세서·세금계산서·예상 매출은 이제 <b>대표 메뉴</b> 안에 있습니다.
                                        핀을 입력하면 열리고, 다 보신 후엔 [잠그기]를 눌러주세요.
                                    </p>
                                )}
                                <p style={styles.pinModalText}>핀을 입력하세요</p>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={pinInput}
                                    onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                                    onKeyDown={handlePinKeyPress}
                                    placeholder="핀 (4~6자리)"
                                    style={styles.pinInput}
                                    autoFocus
                                />
                                {pinError && <p style={styles.pinError}>{pinError}</p>}
                                <button onClick={() => { localStorage.setItem('dcOwnerNoticeSeen', '1'); verifyPin(); }} disabled={pinVerifying || pinInput.length < 4}
                                    style={{ ...styles.pinBtn, opacity: (pinVerifying || pinInput.length < 4) ? 0.5 : 1 }}>
                                    {pinVerifying ? '확인 중…' : '확인'}
                                </button>
                                <button onClick={startPinReset} disabled={pinVerifying}
                                    style={{ marginTop: 8, border: 'none', background: 'transparent', color: '#6366f1', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}>
                                    핀을 잊으셨나요? 이메일로 재설정
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* PIN 설정 안내 모달 (PIN 미설정 사용자가 설정 메뉴 진입 시 표시) */}
            {showPinSetupRequiredModal && (
                <div style={styles.pinModalOverlay}>
                    <div style={styles.pinModal} onClick={e => e.stopPropagation()}>
                        <button style={styles.pinModalClose} onClick={goToProfileForPinSetup}><X size={20} /></button>
                        <div style={{ ...styles.pinIconBox, backgroundColor: '#fef3c7' }}>
                            <Lock size={32} color="#f59e0b" />
                        </div>
                        <h2 style={styles.pinModalTitle}>PIN 설정이 필요합니다</h2>
                        <p style={{ ...styles.pinModalText, lineHeight: 1.6, marginBottom: 20 }}>
                            설정·정산·경영관리 메뉴는<br />
                            <strong style={{ color: '#0f172a' }}>4자리 PIN 인증</strong>이 필요합니다.<br /><br />
                            <span style={{ color: '#94a3b8', fontSize: 13 }}>
                                중요한 정보를 보호하기 위해<br />
                                먼저 프로필에서 PIN을 설정해주세요.
                            </span>
                        </p>
                        <button
                            onClick={goToProfileForPinSetup}
                            style={{ ...styles.pinBtn, marginBottom: 8 }}
                        >
                            프로필로 이동하여 PIN 설정
                        </button>
                    </div>
                </div>
            )}

            {/* 정보 모달 */}
            {showInfoModal && (
                <div style={styles.infoModalOverlay} onClick={() => setShowInfoModal(false)}>
                    <div style={styles.infoModal} onClick={e => e.stopPropagation()}>
                        <button style={styles.infoModalClose} onClick={() => setShowInfoModal(false)}><X size={20} /></button>
                        <h2 style={styles.infoModalTitle}>덴탈클러스터</h2>
                        <p style={styles.infoModalText}>대표: 김승우 | 사업자등록번호: 318-81-11805 | 주소: {BIZ_ADDRESS} | 문의 010-2972-3733</p>
                        <div style={styles.infoModalLinks}>
                            <a href="/terms" style={styles.infoLink}>이용약관</a>
                            <span style={styles.infoLinkDivider}>|</span>
                            <a href="/privacy" style={styles.infoLink}>개인정보처리방침</a>
                            <span style={styles.infoLinkDivider}>|</span>
                            <a href="/account/delete" style={styles.infoLink}>계정삭제</a>
                        </div>
                        <p style={styles.infoModalVersion}>2026.07</p>
                    </div>
                </div>
            )}

            {!isEmbedded && (
                <button style={styles.mobileMenuBtn} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            )}

            {!isEmbedded && isMobileMenuOpen && (
                <div style={styles.mobileOverlay} onClick={() => setIsMobileMenuOpen(false)}>
                    <div style={styles.mobileMenu} onClick={e => e.stopPropagation()}>
                        <div style={styles.mobileHeader}>
                            <span style={styles.mobileTitle}>{userData?.businessName || userData?.name}</span>
                            <button onClick={() => setIsMobileMenuOpen(false)} style={styles.mobileCloseBtn}><X size={24} /></button>
                        </div>
                        {categoryKeys.map(key => {
                            const category = menus[key];
                            if (!category?.items?.length) return null;   // 단계 게이트로 전부 숨겨진 그룹(외주·재택 등)은 모바일에서도 표시 안 함 (2026-08-20)
                            const Icon = category.icon;
                            const isMobileLocked = pinProtectedGroups.includes(key) && needsPinForManagement();
                            // 2026-07-30: owner 구역 잠금 시 모바일에서도 항목명 숨김 + 큰 열기 버튼
                            if (key === 'owner' && isMobileLocked) {
                                return (
                                    <div key={key} style={styles.mobileSection}>
                                        <button onClick={() => { setPendingNavigationPath(null); openPinModal(); }}
                                            style={{ width: '100%', textAlign: 'left', padding: '14px', border: '1px dashed #f59e0b', borderRadius: 10, background: '#FFFBEB', cursor: 'pointer' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 14, color: '#92400E' }}>
                                                <Lock size={15} /> 정산·설정 — 눌러서 열기
                                            </div>
                                            <div style={{ fontSize: 12, color: '#B45309', marginTop: 3 }}>거래명세서 · 예상 매출 · 거래처 · 설정</div>
                                        </button>
                                    </div>
                                );
                            }
                            const isMobileOwnerOpen = key === 'owner' && !isMobileLocked && isBusinessOwner && !isSeller && !isAdmin;
                            return (
                                <div key={key} style={styles.mobileSection}>
                                    <div style={styles.mobileSectionTitle}>
                                        <Icon size={18} /><span>{category.name}</span>
                                        {isMobileLocked && <Lock size={12} color="#f59e0b" style={{ marginLeft: 4 }} />}
                                        {isMobileOwnerOpen && (
                                            <button onClick={lockOwnerZone}
                                                style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
                                                🔓 잠그기
                                            </button>
                                        )}
                                    </div>
                                    {category.items.map(item => (
                                        <button key={item.path} onClick={() => {
                                            if (isClinicOrLabUser) {
                                                handleSidebarItemClick(key, item);
                                            } else if (item.soon) {
                                                alert(item.soonText || '이 기능은 준비 중입니다.');   // 준비중 항목은 이동 없음 (2026-10-30)
                                            } else {
                                                navigate(item.path);
                                                setIsMobileMenuOpen(false);
                                            }
                                        }}
                                            style={{ ...styles.mobileItem, backgroundColor: isActive(item.path) ? '#eef2ff' : 'transparent', color: isActive(item.path) ? '#6366f1' : '#334155' }}>
                                            <span style={{ flex: 1 }}>{item.name}</span>
                                            {item.path === '/patients' && roadmapInboxCount > 0 && (
                                                <span style={{ background: '#eef2ff', color: '#4338ca', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center', marginRight: 4 }}>{roadmapInboxCount}</span>
                                            )}
                                            {item.path === '/patients/bookings' && bookingNewCount > 0 && (
                                                <span style={{ background: '#eef2ff', color: '#4338ca', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center', marginRight: 4 }}>{bookingNewCount}</span>
                                            )}
                                            {item.path === '/chat' && chatUnreadCount > 0 && (
                                                <span style={{ background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center', marginRight: 4 }}>{chatUnreadCount}</span>
                                            )}
                                            <ChevronRight size={16} color="#cbd5e1" />
                                        </button>
                                    ))}
                                </div>
                            );
                        })}
                        <button onClick={handleLogout} style={styles.mobileLogout}><LogOut size={18} /><span>로그아웃</span></button>
                    </div>
                </div>
            )}
            
            {/* 알림 권한 요청 모달 */}
            <NotificationPermissionModal
                isOpen={showNotificationModal}
                onClose={handleCloseNotificationModal}
                userId={userData?.uid}
            />

            {/* 치과 자동 매칭 후보 선택 모달 (가입 직후 1회) */}
            {showCandidatePicker && (
                <HospitalCandidatePicker
                    candidates={matchCandidates}
                    businessName={userData?.businessName}
                    onClose={() => setShowCandidatePicker(false)}
                    onClaimed={() => {
                        setShowCandidatePicker(false);
                        // 매칭 완료 → 잠시 후 새로고침해서 사이드바 등 갱신
                        setTimeout(() => window.location.reload(), 800);
                    }}
                />
            )}
        </div>
    );
}

const styles = {
    container: { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' },
    // ── 기존 80px 아이콘 사이드바 (관리자/판매자/재료회사/개인) ──
    sidebar: { width: 80, backgroundColor: '#fff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100 },
    logo: { width: 48, height: 48, backgroundColor: '#eef2ff', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 },
    categoryList: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8, width: '100%', padding: '0 8px' },
    categoryBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 4px', border: 'none', borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s' },
    categoryName: { fontSize: 10, fontWeight: 600 },
    sidebarFooter: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 16, borderTop: '1px solid #e2e8f0', width: '100%' },
    logoutBtn: { padding: 10, backgroundColor: 'transparent', border: 'none', borderRadius: 10, color: '#94a3b8', cursor: 'pointer' },
    // ── 새 200px 텍스트 사이드바 (치과/기공소) ──
    newSidebar: { width: 214, backgroundColor: '#fff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100, fontFamily: 'system-ui, -apple-system, "Segoe UI", "Apple SD Gothic Neo", sans-serif' },
    newSidebarHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '14px', borderBottom: '1px solid #e2e8f0' },
    newSidebarTitle: { fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
    newSidebarBadge: { fontSize: 10, color: '#6366f1', backgroundColor: '#eef2ff', padding: '2px 6px', borderRadius: 4, fontWeight: 600 },
    newSidebarBody: { flex: 1, overflowY: 'auto', padding: '8px 0' },
    sidebarGroup: { marginBottom: 4 },
    sidebarGroupTitle: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px 4px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 },
    sidebarItem: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 16px 8px 20px', border: 'none', backgroundColor: 'transparent', fontSize: 13, cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left', borderRadius: 0, fontFamily: 'inherit' },
    // 아이콘 자리를 대신하는 점 (시안 labcost_preview .item .dot)
    sidebarDot: { width: 5, height: 5, borderRadius: '50%', flex: '0 0 5px', display: 'block' },
    newSidebarFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderTop: '1px solid #e2e8f0' },
    newSidebarLogout: { padding: '2px 0', border: 'none', backgroundColor: 'transparent', color: '#94a3b8', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' },
    newSidebarInfo: { padding: '2px 0', border: 'none', backgroundColor: 'transparent', color: '#94a3b8', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
    main: { flex: 1, marginLeft: 80, display: 'flex', flexDirection: 'column' },
    content: { flex: 1, padding: 0, overflow: 'auto' },
    contentHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', marginBottom: 0 },
    tabList: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    tabBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' },
    userInfo: { display: 'flex', alignItems: 'center', gap: 8 },
    userName: { fontSize: 14, fontWeight: 600, color: '#334155' },
    userType: { fontSize: 12, color: '#6366f1', backgroundColor: '#eef2ff', padding: '4px 8px', borderRadius: 6 },
    quickFindBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', marginRight: 8 },
    quickHeaderBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 },
    pinModalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    pinModal: { backgroundColor: '#fff', borderRadius: 16, padding: '40px 32px', textAlign: 'center', maxWidth: 360, width: '90%', position: 'relative' },
    pinModalClose: { position: 'absolute', top: 12, right: 12, padding: 8, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' },
    pinIconBox: { width: 72, height: 72, backgroundColor: '#eef2ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
    pinModalTitle: { fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
    pinModalText: { fontSize: 14, color: '#64748b', marginBottom: 24 },
    pinInput: { width: '100%', padding: '14px 16px', fontSize: 24, textAlign: 'center', border: '2px solid #e2e8f0', borderRadius: 12, outline: 'none', letterSpacing: 12, marginBottom: 16 },
    pinError: { color: '#ef4444', fontSize: 14, marginBottom: 16 },
    pinBtn: { width: '100%', padding: '14px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' },
    mobileMenuBtn: { display: 'none', position: 'fixed', bottom: 20, right: 20, width: 56, height: 56, backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '50%', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)', cursor: 'pointer', zIndex: 200, alignItems: 'center', justifyContent: 'center' },
    mobileOverlay: { display: 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 300 },
    mobileMenu: { position: 'absolute', top: 0, right: 0, width: '80%', maxWidth: 320, height: '100%', backgroundColor: '#fff', overflowY: 'auto' },
    mobileHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottom: '1px solid #e2e8f0' },
    mobileTitle: { fontSize: 18, fontWeight: 700, color: '#0f172a' },
    mobileCloseBtn: { padding: 8, backgroundColor: 'transparent', border: 'none', cursor: 'pointer' },
    mobileSection: { borderBottom: '1px solid #f1f5f9' },
    mobileSectionTitle: { display: 'flex', alignItems: 'center', gap: 8, padding: '16px 20px 8px', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' },
    mobileItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '14px 20px', border: 'none', backgroundColor: 'transparent', fontSize: 15, cursor: 'pointer', textAlign: 'left' },
    infoModalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    infoModal: { backgroundColor: '#fff', borderRadius: 16, padding: '40px 32px', textAlign: 'center', maxWidth: 400, width: '90%', position: 'relative' },
    infoModalClose: { position: 'absolute', top: 12, right: 12, padding: 8, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' },
    infoModalTitle: { fontSize: 24, fontWeight: 700, color: '#6366f1', marginBottom: 16 },
    infoModalText: { fontSize: 14, color: '#64748b', marginBottom: 24 },
    infoModalLinks: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 },
    infoLink: { color: '#6366f1', textDecoration: 'none', fontSize: 14, fontWeight: 500 },
    infoLinkDivider: { color: '#cbd5e1' },
    infoModalVersion: { fontSize: 14, color: '#94a3b8' },
    mobileLogout: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '16px 20px', border: 'none', backgroundColor: 'transparent', color: '#ef4444', fontSize: 15, fontWeight: 600, cursor: 'pointer' }
};

export { communityItemsData };
export default MainLayout;